// Integración de la gestión de capacidad en la vista de Ingresos

<template>
  <div class="ingresos-container">
    <!-- Tabla de Ingresos -->
    <v-card>
      <v-card-title class="d-flex justify-space-between align-center flex-wrap">
        <span class="ingresos-title">Gestión de Ingresos</span>
        <v-btn
          color="primary"
          prepend-icon="mdi-plus"
          @click="showNewIngresoModal"
          size="default"
        >
          <span class="d-none d-sm-inline">Nuevo Ingreso</span>
          <span class="d-sm-none">Nuevo</span>
        </v-btn>
      </v-card-title>

      <v-card-text>
        <!-- Filtros -->
        <v-row class="mb-4 ingresos-filters">
          <v-col cols="12" sm="6" md="3">
            <v-select
              v-model="filters.status"
              :items="[
                { title: 'Todos', value: '' },
                { title: 'Completado', value: 'Completado' },
                { title: 'Pendiente', value: 'Pendiente' },
                { title: 'Cancelado', value: 'Cancelado' }
              ]"
              label="Estado"
              hide-details
              density="compact"
              style="max-width: 100%;"
            ></v-select>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-text-field
              v-model="filters.dateFrom"
              label="Fecha desde"
              type="date"
              hide-details
              density="compact"
              style="max-width: 100%;"
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-text-field
              v-model="filters.dateTo"
              label="Fecha hasta"
              type="date"
              hide-details
              density="compact"
              style="max-width: 100%;"
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-text-field
              v-model="filters.search"
              prepend-inner-icon="mdi-magnify"
              label="Buscar"
              placeholder="Referencia, proveedor..."
              hide-details
              density="compact"
              style="max-width: 100%;"
            ></v-text-field>
          </v-col>
        </v-row>

        <!-- Tabla -->
        <ResponsiveDataTable
          :items="filteredIngresos"
          :headers="[
            { title: 'Referencia', key: 'reference', align: 'start', sortable: true },
            { title: 'Fecha', key: 'created_at', align: 'start', sortable: true },
            { title: 'Proveedor', key: 'supplier', align: 'start', sortable: true },
            { title: 'SKUs', key: 'items_count', align: 'end', sortable: true },
            { title: 'Notas', key: 'notes', align: 'start', sortable: false },
            { title: 'Estado', key: 'status', align: 'center', sortable: true },
            { title: 'Acciones', key: 'actions', align: 'center', sortable: false }
          ]"
          :loading="loading"
          :search="filters.search"
          class="elevation-1 ingresos-table"
          :show-select="false"
          items-per-page="10"
          density="default"
          hover
        >
          <template v-slot:item.created_at="{ item }">
            {{ formatDate(item.created_at) }}
          </template>

          <template v-slot:item.supplier="{ item }">
            {{ item.supplier || 'No especificado' }}
          </template>

          <template v-slot:item.items_count="{ item }">
            {{ item.details?.length || 0 }}
          </template>

          <template v-slot:item.notes="{ item }">
            {{ item.notes || 'Sin notas' }}
          </template>

          <template v-slot:item.status="{ item }">
            <v-chip
              :color="getStatusColor(item.status)"
              text-color="white"
              size="small"
            >
              {{ item.status }}
            </v-chip>
          </template>

          <template v-slot:item.actions="{ item }">
            <v-btn
              icon="mdi-eye"
              size="small"
              variant="text"
              color="info"
              @click="viewIngreso(item)"
              :disabled="!item"
            ></v-btn>
            <v-btn
              icon="mdi-pencil"
              size="small"
              variant="text"
              color="primary"
              @click="editIngreso(item)"
              :disabled="!item || !['Pendiente'].includes(item.status)"
            ></v-btn>
            <v-btn
              icon="mdi-package-down"
              size="small"
              variant="text"
              color="success"
              @click="createStorageList(item)"
              :disabled="!item || item.status !== 'Pendiente'"
              title="Crear Lista de Almacenamiento"
            ></v-btn>
            <v-btn
              icon="mdi-delete"
              size="small"
              variant="text"
              color="error"
              @click="deleteIngreso(item)"
              :disabled="!item || !['Pendiente'].includes(item.status)"
            ></v-btn>
          </template>

          <template v-slot:no-data>
            <div class="text-center py-4">
              <v-icon icon="mdi-alert" color="warning" size="large"></v-icon>
              <p class="text-subtitle-1 mt-2">No se encontraron ingresos</p>
            </div>
          </template>
        </ResponsiveDataTable>
      </v-card-text>
    </v-card>
    
    <!-- Modal de Nuevo Ingreso -->
    <v-dialog v-model="showCreateModal" max-width="1000" persistent>
      <v-card v-if="safeNewIngresoItems.length > 0">
        <v-card-title class="d-flex justify-space-between align-center">
          Nuevo Ingreso
          <v-chip
            :color="simplifiedMode ? 'success' : 'primary'"
            label
            size="small"
          >
            {{ simplifiedMode ? 'Modo Simplificado' : 'Modo Tradicional' }}
          </v-chip>
        </v-card-title>
        
        <!-- Toggle de modo de ingreso -->
        <v-card-text class="pb-0">
          <v-alert 
            :type="simplifiedMode ? 'success' : 'info'"
            variant="tonal"
            density="compact"
            class="mb-4"
          >
            <div class="d-flex align-center justify-space-between">
              <div>
                <v-icon class="mr-2">{{ simplifiedMode ? 'mdi-lightning-bolt' : 'mdi-account-hard-hat' }}</v-icon>
                <strong>
                  {{ simplifiedMode ? 'Modo Simplificado:' : 'Modo Tradicional:' }}
                </strong>
                {{ simplifiedMode 
                   ? 'Los productos se agregan directamente al inventario' 
                   : 'Se asigna trabajo de almacenamiento al bodeguero' }}
              </div>
              <v-switch
                v-model="simplifiedMode"
                :label="simplifiedMode ? 'Simplificado' : 'Tradicional'"
                color="success"
                hide-details
                density="compact"
                inset
              ></v-switch>
            </div>
          </v-alert>
        </v-card-text>
        
        <v-card-text>
          <v-form ref="createForm" @submit.prevent="submitIngreso">
            <v-row>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="newIngreso.order_number"
                  label="Número de Orden"
                  :rules="[v => !!v || 'El número de orden es requerido']"
                  required
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="newIngreso.supplier"
                  label="Proveedor"
                  placeholder="Nombre del proveedor"
                ></v-text-field>
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12" md="6">
                <v-select
                  v-model="newIngreso.warehouse_id"
                  :items="warehouses"
                  item-title="name"
                  item-value="id"
                  label="Bodega"
                  :rules="[v => !!v || 'La bodega es requerida']"
                  @update:model-value="loadZones"
                  required
                ></v-select>
              </v-col>
              <v-col cols="12" md="6">
                <v-select
                  v-model="newIngreso.zone_id"
                  :items="filteredZones"
                  item-title="name"
                  item-value="id"
                  label="Zona"
                  :rules="[v => !!v || 'La zona es requerida']"
                  :disabled="!newIngreso.warehouse_id"
                  required
                ></v-select>
              </v-col>
            </v-row>

            <v-textarea
              v-model="newIngreso.notes"
              label="Notas"
              rows="3"
            ></v-textarea>

            <v-divider class="my-4"></v-divider>

            <h3 class="text-h6 mb-4">Productos</h3>
            <v-row 
              v-for="(item, index) in safeNewIngresoItems" 
              :key="`item-${index}`" 
              class="mb-2"
            >
              <v-col cols="12" md="3">
                <div class="d-flex align-center">
                  <v-autocomplete
                    v-model="item.product_id"
                    :items="getFilteredProducts(index)"
                    item-title="name"
                    item-value="id"
                    label="Producto"
                    :rules="[v => !!v || 'El producto es requerido']"
                    required
                    clearable
                    :search="item.productSearch"
                    @update:search="(search) => handleProductSearch(search, index)"
                    @update:model-value="handleProductSelection(index, $event)"
                    :menu-props="{ maxHeight: 300, closeOnContentClick: false }"
                    :no-data-text="(item.productSearch && item.productSearch.length >= 2) ? 'No se encontraron productos' : 'Escribe SKU o nombre para buscar...'"
                    :custom-filter="() => true"
                    auto-select-first
                    hide-no-data
                  >
                    <template v-slot:item="{ props, item: productItem }">
                      <v-list-item v-bind="props">
                        <v-list-item-title>{{ productItem.raw.name }}</v-list-item-title>
                        <v-list-item-subtitle>SKU: {{ productItem.raw.sku }}</v-list-item-subtitle>
                      </v-list-item>
                    </template>
                  </v-autocomplete>
                  <v-btn
                    icon="mdi-barcode-scan"
                    color="primary"
                    class="ms-2"
                    @click="startScan(index)"
                  ></v-btn>
                  <!-- Botón de impresión para productos con código EAN-13 -->
                  <v-btn
                    v-if="item.product_id && getProductBarcode(item.product_id) && isEAN13(getProductBarcode(item.product_id))"
                    icon="mdi-printer"
                    color="success"
                    class="ms-1"
                    @click="showPrintProductDialog(item.product_id, item.quantity)"
                    :title="`Imprimir código de barras EAN-13: ${getProductBarcode(item.product_id)}`"
                  ></v-btn>
                  <!-- Indicador de producto escaneado con GS1 -->
                  <v-chip 
                    v-if="item.auto_filled && item.barcode_type === 'GS1'"
                    size="small" 
                    color="success" 
                    variant="outlined"
                    class="ms-2"
                  >
                    GS1
                  </v-chip>
                </div>
              </v-col>
              <v-col cols="12" md="2">
                <v-text-field
                  v-model.number="item.quantity"
                  type="number"
                  label="Cantidad"
                  min="1"
                  :rules="[
                    v => !!v || 'La cantidad es requerida',
                    v => v > 0 || 'La cantidad debe ser mayor a 0'
                  ]"
                  required
                  @update:model-value="handleQuantityChange(index)"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="2">
                <v-text-field
                  v-model="item.lot_number"
                  label="Lote"
                  placeholder="Lote"
                  clearable
                  :prepend-inner-icon="item.auto_filled && item.lot_number ? 'mdi-barcode' : undefined"
                  :color="item.auto_filled && item.lot_number ? 'success' : undefined"
                  :hint="item.auto_filled && item.lot_number ? 'Auto-llenado desde GS1' : undefined"
                  :persistent-hint="item.auto_filled && item.lot_number"
                ></v-text-field>
              </v-col>
              <!-- Campo S/N (Serial Number) - Solo visible cuando cantidad = 1 -->
              <v-col v-if="item.quantity === 1" cols="12" md="2">
                <v-text-field
                  v-model="item.serial_number"
                  label="S/N"
                  placeholder="S/N"
                  clearable
                  :prepend-inner-icon="item.auto_filled && item.serial_number ? 'mdi-barcode' : undefined"
                  :color="item.auto_filled && item.serial_number ? 'success' : undefined"
                  :hint="item.auto_filled && item.serial_number ? 'Auto-llenado desde GS1' : undefined"
                  :persistent-hint="item.auto_filled && item.serial_number"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="2">
                <v-text-field
                  v-model="item.expiry_date"
                  label="Fecha de Vencimiento"
                  type="date"
                  :rules="[v => !!v || 'La fecha de vencimiento es requerida']"
                  required
                  :prepend-inner-icon="item.auto_filled && item.expiry_date && item.expiry_date !== new Date().toISOString().split('T')[0] ? 'mdi-barcode' : undefined"
                  :color="item.auto_filled && item.expiry_date && item.expiry_date !== new Date().toISOString().split('T')[0] ? 'success' : undefined"
                  :hint="item.auto_filled && item.expiry_date && item.expiry_date !== new Date().toISOString().split('T')[0] ? 'Auto-llenado desde GS1' : undefined"
                  :persistent-hint="item.auto_filled && item.expiry_date && item.expiry_date !== new Date().toISOString().split('T')[0]"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="1" class="d-flex align-center">
                <v-btn
                  icon="mdi-delete"
                  color="error"
                  variant="text"
                  @click="removeItem(index)"
                  :disabled="newIngreso.items.length === 1"
                ></v-btn>
              </v-col>
              <!-- Sección de Dispositivos Médicos - Solo visible para productos DM priorizados -->
              <v-row v-if="item.product_id && isProductDMPriorizado(item.product_id)" class="mt-2 mb-2">
                <v-col cols="12">
                  <v-alert
                    type="info"
                    variant="tonal"
                    density="compact"
                    class="mb-2"
                  >
                    <div class="d-flex align-center">
                      <v-icon class="mr-2" color="red">mdi-medical-bag</v-icon>
                      <strong>Dispositivo Médico Priorizado</strong>
                      <span class="ml-2 text-caption">(Norma Técnica N° 247)</span>
                    </div>
                  </v-alert>
                </v-col>
                
                <!-- Forma de Adquisición -->
                <v-col cols="12" md="3">
                  <v-select
                    v-model="item.forma_adquisicion"
                    :items="formasAdquisicion"
                    label="Forma de Adquisición *"
                    :rules="[v => !!v || 'Forma de adquisición es requerida para DM']"
                    variant="outlined"
                    density="compact"
                    required
                  ></v-select>
                </v-col>
                
                <!-- Fecha Recepción Conforme -->
                <v-col cols="12" md="3">
                  <v-text-field
                    v-model="item.fecha_recepcion_conforme"
                    label="Fecha Recepción Conforme *"
                    type="datetime-local"
                    :rules="[v => !!v || 'Fecha de recepción conforme es requerida para DM']"
                    variant="outlined"
                    density="compact"
                    required
                  ></v-text-field>
                </v-col>
                
                <!-- Número de Registro Sanitario - Solo si el producto lo requiere -->
                <v-col v-if="isProductDMPriorizado(item.product_id) && getProductRequiereRegistroSanitario(item.product_id)" cols="12" md="3">
                  <v-text-field
                    v-model="item.numero_registro_sanitario"
                    label="N° Registro Sanitario ISP *"
                    placeholder="Ej: 12345-AB/CD"
                    :rules="[v => !!v || 'Número de registro sanitario es requerido para este DM']"
                    variant="outlined"
                    density="compact"
                    required
                  ></v-text-field>
                </v-col>
                
                <!-- Número Inventario Activo Fijo - Solo para equipos -->
                <v-col v-if="item.product_id && getProductInfo(item.product_id)?.tipo_dm === 'Equipo Médico Crítico'" cols="12" md="3">
                  <v-text-field
                    v-model="item.numero_inventario_activo_fijo"
                    label="N° Inventario Activo Fijo"
                    placeholder="Opcional para equipos"
                    variant="outlined"
                    density="compact"
                  ></v-text-field>
                </v-col>
              </v-row>
            </v-row>

            <!-- Sección de Escaneo Múltiple -->
            <div v-if="isMultipleScanMode" class="mt-4 pa-4 border rounded">
              <div class="d-flex justify-space-between align-center mb-3">
                <h6 class="text-h6 d-flex align-center">
                  <v-icon icon="mdi-barcode-scan" class="mr-2"></v-icon>
                  Modo Escaneo Múltiple
                </h6>
                <v-chip
                  v-if="scannedProducts.length > 0"
                  color="primary"
                  variant="tonal"
                  size="small"
                >
                  {{ scannedProducts.length }} productos | {{ scannedProducts.reduce((sum, p) => sum + p.quantity, 0) }} unidades
                </v-chip>
              </div>

              <v-alert
                type="info"
                variant="tonal"
                class="mb-3"
                density="compact"
              >
                <div class="text-body-2">
                  Use su lector de códigos de barras o escriba manualmente. Los productos se agregarán automáticamente a la lista.
                </div>
              </v-alert>

              <!-- Campo de entrada para códigos de barras -->
              <v-text-field
                ref="multipleScanInput"
                v-model="multipleScanInput"
                label="Escanee aquí o escriba el código de barras"
                prepend-inner-icon="mdi-barcode-scan"
                variant="outlined"
                density="comfortable"
                autofocus
                clearable
                @keydown.enter="processScannedBarcode"
                @input="handleBarcodeInput"
                :loading="scanStatus.type === 'loading'"
                :color="scanStatus.type === 'success' ? 'success' : scanStatus.type === 'error' ? 'error' : 'primary'"
              >
                <template v-slot:append>
                  <v-btn
                    v-if="scannedProducts.length > 0"
                    icon="mdi-undo"
                    color="warning"
                    variant="text"
                    @click="undoLastScan"
                    :title="'Deshacer: ' + (lastScannedProduct ? lastScannedProduct.name : '')"
                  ></v-btn>
                </template>
              </v-text-field>

              <!-- Mensaje de estado -->
              <v-alert
                v-if="scanStatus.message"
                :type="scanStatus.type === 'loading' ? 'info' : scanStatus.type"
                variant="tonal"
                class="mt-2"
                density="compact"
                dismissible
              >
                {{ scanStatus.message }}
              </v-alert>

              <!-- Lista compacta de productos escaneados -->
              <div v-if="scannedProducts.length > 0" class="mt-3">
                <div class="d-flex justify-space-between align-center mb-2">
                  <small class="text-grey">Últimos productos escaneados:</small>
                  <v-btn
                    v-if="scannedProducts.length > 0"
                    color="warning"
                    variant="text"
                    size="small"
                    @click="clearScannedProducts"
                  >
                    Limpiar Todo
                  </v-btn>
                </div>
                <div class="d-flex flex-wrap gap-2">
                  <v-chip
                    v-for="(product, index) in scannedProducts.slice(-5)"
                    :key="product.id + '-' + index"
                    color="success"
                    variant="tonal"
                    size="small"
                  >
                    {{ product.name }} ({{ product.quantity }})
                  </v-chip>
                  <v-chip
                    v-if="scannedProducts.length > 5"
                    color="grey"
                    variant="text"
                    size="small"
                  >
                    +{{ scannedProducts.length - 5 }} más
                  </v-chip>
                </div>
              </div>
            </div>

            <div class="d-flex justify-space-between align-center mt-4">
              <v-btn
                :color="isMultipleScanMode ? 'success' : 'secondary'"
                :variant="isMultipleScanMode ? 'elevated' : 'outlined'"
                :prepend-icon="isMultipleScanMode ? 'mdi-check' : 'mdi-barcode-scan'"
                @click="toggleMultipleScanMode"
              >
                {{ isMultipleScanMode ? 'Finalizar Escaneo' : 'Escanear Múltiples' }}
              </v-btn>
              <v-btn
                color="primary"
                variant="text"
                prepend-icon="mdi-plus"
                @click="addItem"
                :disabled="isMultipleScanMode"
              >
                Agregar Producto
              </v-btn>
            </div>

            <v-alert
              v-if="!hasCapacity"
              type="warning"
              class="mt-4"
            >
              La zona seleccionada no tiene capacidad suficiente para este ingreso
            </v-alert>
            
            <v-alert
              v-if="hasCapacity && !areDMFieldsComplete"
              type="error"
              class="mt-4"
              variant="tonal"
            >
              <v-icon icon="mdi-alert-circle" class="mr-2"></v-icon>
              Por favor complete todos los campos requeridos de los Dispositivos Médicos priorizados antes de crear el ingreso.
            </v-alert>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" variant="text" @click="closeCreateModal">
            Cancelar
          </v-btn>
          <v-btn
            color="primary"
            :loading="submitting"
            :disabled="!canCreateIngreso"
            @click="submitIngreso"
          >
            Crear Ingreso
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    
    <!-- Modal de Editar Ingreso -->
    <v-dialog v-model="showEditModal" max-width="1000" persistent>
      <v-card v-if="safeEditIngresoItems.length > 0">
        <v-card-title>{{ isEditingIngreso ? 'Editar Ingreso' : 'Nuevo Ingreso' }}</v-card-title>
        <v-card-text>
          <v-form ref="editForm" @submit.prevent="saveEditedIngreso">
            <v-row>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="editingIngreso.reference"
                  label="Número de Orden"
                  :rules="[v => !!v || 'El número de orden es requerido']"
                  required
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="editingIngreso.supplier"
                  label="Proveedor"
                  placeholder="Nombre del proveedor"
                ></v-text-field>
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12" md="6">
                <v-select
                  v-model="editingIngreso.status"
                  :items="statusOptions"
                  label="Estado"
                  :rules="[v => !!v || 'El estado es requerido']"
                  required
                ></v-select>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="editingIngreso.created_at"
                  label="Fecha de Creación"
                  type="date"
                  readonly
                ></v-text-field>
              </v-col>
            </v-row>

            <v-textarea
              v-model="editingIngreso.notes"
              label="Notas"
              rows="3"
            ></v-textarea>

            <v-divider class="my-4"></v-divider>

            <h3 class="text-h6 mb-4">Productos</h3>
            <v-row 
              v-for="(item, index) in safeEditIngresoItems" 
              :key="`edit-${index}`" 
              class="mb-2"
            >
              <v-col cols="12" md="3">
                <div class="d-flex align-center">
                  <v-autocomplete
                    v-model="item.product_id"
                    :items="getFilteredProductsEdit(index)"
                    item-title="name"
                    item-value="id"
                    label="Producto"
                    :rules="[v => !!v || 'El producto es requerido']"
                    required
                    clearable
                    :search="item.productSearch"
                    @update:search="(search) => handleProductSearchEdit(search, index)"
                    @update:model-value="handleProductSelectionEdit(index, $event)"
                    :menu-props="{ maxHeight: 300, closeOnContentClick: false }"
                    :custom-filter="() => true"
                    auto-select-first
                    hide-no-data
                  >
                    <template v-slot:item="{ props, item: productItem }">
                      <v-list-item v-bind="props">
                        <v-list-item-title>{{ productItem.raw.name }}</v-list-item-title>
                        <v-list-item-subtitle>SKU: {{ productItem.raw.sku }}</v-list-item-subtitle>
                      </v-list-item>
                    </template>
                  </v-autocomplete>
                  <v-btn
                    icon="mdi-barcode-scan"
                    color="primary"
                    class="ms-2"
                    @click="startScanEdit(index)"
                  ></v-btn>
                  <!-- Botón de impresión para productos con código EAN-13 en modo edición -->
                  <v-btn
                    v-if="item.product_id && getProductBarcode(item.product_id) && isEAN13(getProductBarcode(item.product_id))"
                    icon="mdi-printer"
                    color="success"
                    class="ms-1"
                    @click="showPrintProductDialog(item.product_id, item.quantity)"
                    :title="`Imprimir código de barras EAN-13: ${getProductBarcode(item.product_id)}`"
                  ></v-btn>
                </div>
              </v-col>
              <v-col cols="12" md="2">
                <v-text-field
                  v-model.number="item.quantity"
                  type="number"
                  label="Cantidad"
                  min="1"
                  :rules="[
                    v => !!v || 'La cantidad es requerida',
                    v => v > 0 || 'La cantidad debe ser mayor a 0'
                  ]"
                  required
                  @update:model-value="handleQuantityChangeEdit(index)"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="2">
                <v-text-field
                  v-model="item.lot_number"
                  label="Lote"
                  placeholder="Lote"
                  clearable
                ></v-text-field>
              </v-col>
              <!-- Campo S/N (Serial Number) - Solo visible cuando cantidad = 1 -->
              <v-col v-if="item.quantity === 1" cols="12" md="2">
                <v-text-field
                  v-model="item.serial_number"
                  label="S/N"
                  placeholder="S/N"
                  clearable
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="2">
                <v-text-field
                  v-model="item.expiry_date"
                  label="Fecha de Vencimiento"
                  type="date"
                  :rules="[v => !!v || 'La fecha de vencimiento es requerida']"
                  required
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="1" class="d-flex align-center">
                <v-btn
                  icon="mdi-delete"
                  color="error"
                  variant="text"
                  @click="removeEditItem(index)"
                  :disabled="editingIngreso.details.length === 1"
                ></v-btn>
              </v-col>
            </v-row>

            <div class="d-flex justify-end mt-4">
              <v-btn
                color="primary"
                variant="text"
                prepend-icon="mdi-plus"
                @click="addEditItem"
              >
                Agregar Producto
              </v-btn>
            </div>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" variant="text" @click="showEditModal = false">
            Cancelar
          </v-btn>
          <v-btn
            color="primary"
            :loading="submitting"
            @click="saveEditedIngreso"
          >
            Guardar Cambios
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    
    <!-- Modal de Detalles de Ingreso -->
    <v-dialog v-model="showDetailsModal" max-width="800">
      <v-card>
        <v-card-title>Detalles del Ingreso</v-card-title>
        <v-card-text v-if="selectedIngreso">
          <v-row>
            <v-col cols="12" md="6">
              <div class="detail-group">
                <label>Número de Orden:</label>
                <p>{{ selectedIngreso.reference }}</p>
              </div>
              <div class="detail-group">
                <label>Fecha:</label>
                <p>{{ formatDate(selectedIngreso.created_at) }}</p>
              </div>
              <div class="detail-group">
                <label>Proveedor:</label>
                <p>{{ selectedIngreso.supplier || 'No especificado' }}</p>
              </div>
              <div class="detail-group">
                <label>Estado:</label>
                <v-chip :color="getStatusColor(selectedIngreso.status)" text-color="white">
                  {{ getStatusText(selectedIngreso.status) }}
                </v-chip>
              </div>
            </v-col>
            <v-col cols="12" md="6">
              <div class="detail-group">
                <label>Tipo:</label>
                <p>{{ selectedIngreso.type }}</p>
              </div>
              <div class="detail-group">
                <label>Notas:</label>
                <p>{{ selectedIngreso.notes || 'Sin notas' }}</p>
              </div>
            </v-col>
          </v-row>

          <v-divider class="my-4"></v-divider>

          <h3 class="text-h6 mb-4">Productos</h3>
          <v-table>
            <thead>
              <tr>
                <th>Producto</th>
                <th>Cantidad</th>
                <th>Lote</th>
                <th>S/N</th>
                <th>Fecha de Vencimiento</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in selectedIngreso.details" :key="item.id">
                <td>{{ item.product ? item.product.name : 'Producto no encontrado' }}</td>
                <td>{{ item.quantity }}</td>
                <td>{{ item.lot_number || '' }}</td>
                <td>{{ item.serial_number || '' }}</td>
                <td>{{ formatDate(item.expiry_date) }}</td>
              </tr>
            </tbody>
          </v-table>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" variant="text" @click="showDetailsModal = false">
            Cerrar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    
    <!-- Modal de Confirmación de Eliminación -->
    <v-dialog v-model="showDeleteModal" max-width="500">
      <v-card>
        <v-card-title>Confirmar Eliminación</v-card-title>
        <v-card-text>
          ¿Está seguro que desea eliminar este ingreso? Esta acción no se puede deshacer.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" variant="text" @click="showDeleteModal = false">
            Cancelar
          </v-btn>
          <v-btn color="error" @click="confirmDeleteIngreso">
            Eliminar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Modal de Escáner -->
    <v-dialog v-model="showBarcodeScanner" max-width="500">
      <v-card>
        <v-card-title>Escanear Código de Barras</v-card-title>
        <v-card-text>
          <div class="text-center">
            <video ref="videoElement" class="barcode-scanner"></video>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="grey"
            variant="text"
            @click="stopScanner"
          >
            Cerrar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Modal de Configuración de Lista de Almacenamiento -->
    <v-dialog v-model="showStorageConfigDialog" max-width="600">
      <v-card>
        <v-card-title>
          <v-icon icon="mdi-cog" class="mr-2"></v-icon>
          Configurar Lista de Almacenamiento
        </v-card-title>
        <v-card-text>
          <div v-if="selectedIngreso" class="mb-4">
            <h6 class="text-h6 mb-2">Ingreso: {{ selectedIngreso.reference }}</h6>
            <p class="text-body-2 text-grey">
              Proveedor: {{ selectedIngreso.supplier || 'No especificado' }}
            </p>
          </div>
          
          <v-divider class="mb-4"></v-divider>
          
          <div class="switch-container">
            <v-switch
              v-model="storageConfig.pieceByPieceEnabled"
              label="Habilitar Almacenamiento Piece-by-Piece"
              color="primary"
              inset
              hide-details
            ></v-switch>
            <p class="text-body-2 text-grey mt-1 ml-12">
              El operario deberá escanear cada producto individualmente antes de almacenar
            </p>
          </div>
          
          <v-alert
            v-if="storageConfig.pieceByPieceEnabled"
            type="info"
            variant="tonal"
            class="mt-4"
          >
            <div class="text-body-2">
              <strong>Almacenamiento Piece-by-Piece Habilitado:</strong>
              <ul class="mt-2">
                <li>El operario deberá escanear cada unidad individual del producto</li>
                <li>Se validará que el código de barras coincida con el producto</li>
                <li>El progreso se actualizará automáticamente cada 5 segundos</li>
                <li>Solo se podrá completar cuando todas las unidades estén escaneadas</li>
              </ul>
            </div>
          </v-alert>
          
          <v-alert
            v-else
            type="warning"
            variant="tonal"
            class="mt-4"
          >
            <div class="text-body-2">
              <strong>Almacenamiento Tradicional:</strong>
              <br>
              El operario escaneará la ubicación y confirmará el producto una sola vez por item.
            </div>
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" variant="text" @click="showStorageConfigDialog = false">
            Cancelar
          </v-btn>
          <v-btn
            color="primary"
            :loading="submitting"
            @click="confirmCreateStorageList"
            prepend-icon="mdi-plus"
          >
            Crear Lista
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Modal de Impresión de Códigos de Barras -->
    <v-dialog v-model="showPrintDialog" max-width="600">
      <v-card>
        <v-card-title>
          Imprimir Código de Barras
          <v-chip color="success" size="small" class="ml-2">
            EAN-13
          </v-chip>
        </v-card-title>
        <v-card-text>
          <v-row>
            <v-col cols="12">
              <div class="text-h6 mb-2">
                {{ selectedProductForPrint?.name }}
              </div>
              <div class="text-subtitle-2 mb-2">
                SKU: {{ selectedProductForPrint?.sku }}
              </div>
              <div class="text-subtitle-2 mb-4">
                Código: {{ selectedProductForPrint?.barcode }}
              </div>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12">
              <v-select
                v-model="printSettings.printerType"
                :items="[
                  { title: 'Impresora Estándar (A4)', value: 'standard' },
                  { title: 'Impresora de Etiquetas (Zebra/Térmica)', value: 'label' }
                ]"
                label="Tipo de Impresora"
              ></v-select>
            </v-col>
          </v-row>

          <v-row v-if="printSettings.printerType === 'standard'">
            <v-col cols="12" md="6">
              <v-select
                v-model="printSettings.labelsPerRow"
                :items="[1, 2, 3, 4]"
                label="Etiquetas por fila"
              ></v-select>
            </v-col>
            <v-col cols="12" md="6">
              <v-select
                v-model="printSettings.labelSize"
                :items="[
                  { title: 'Pequeño (2x1 cm)', value: 'small' },
                  { title: 'Mediano (4x2 cm)', value: 'medium' },
                  { title: 'Grande (6x3 cm)', value: 'large' }
                ]"
                label="Tamaño de etiqueta"
              ></v-select>
            </v-col>
          </v-row>

          <v-row v-if="printSettings.printerType === 'label'">
            <v-col cols="12" md="6">
              <v-select
                v-model="printSettings.labelFormat"
                :items="[
                  { title: '2x1 pulgadas', value: '2x1' },
                  { title: '4x2 pulgadas', value: '4x2' },
                  { title: '4x6 pulgadas', value: '4x6' }
                ]"
                label="Formato de etiqueta"
              ></v-select>
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                v-model.number="printSettings.dpi"
                type="number"
                label="DPI"
                hint="Resolución de la impresora (203, 300, 600)"
              ></v-text-field>
            </v-col>
          </v-row>

          <!-- Información automática de cantidad -->
          <v-row>
            <v-col cols="12">
              <v-alert type="info" variant="tonal" class="mb-3">
                <strong>Cantidad a imprimir:</strong> {{ selectedProductForPrint?.quantity || 1 }} código(s) de barras
                <br>
                <small>Se imprimirá automáticamente la cantidad del producto</small>
              </v-alert>
            </v-col>
          </v-row>

          <v-divider class="my-4"></v-divider>

          <div class="text-subtitle-2 mb-2">Vista Previa:</div>
          <div class="print-preview-container" :class="printSettings.printerType">
            <div 
              v-for="copy in (selectedProductForPrint?.quantity || 1)" 
              :key="copy"
              class="barcode-label preview-label"
              :class="printSettings.labelSize"
            >
              <div class="barcode-visual">
                <canvas 
                  :ref="'previewCanvas' + copy"
                  class="preview-barcode-canvas"
                ></canvas>
              </div>
              <div class="barcode-text">{{ selectedProductForPrint?.barcode }}</div>
            </div>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" variant="text" @click="showPrintDialog = false">
            Cancelar
          </v-btn>
          <v-btn color="primary" @click="printProductBarcode" :loading="printing">
            <v-icon left>mdi-printer</v-icon>
            Imprimir
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Modal de confirmación para crear producto no encontrado -->
    <v-dialog v-model="showCreateProductConfirm" max-width="500" persistent>
      <v-card>
        <v-card-title class="text-h6 d-flex align-center">
          <v-icon color="warning" class="mr-2">mdi-package-variant-plus</v-icon>
          Producto No Encontrado
        </v-card-title>
        <v-card-text>
          <v-alert type="warning" variant="tonal" class="mb-4">
            <div class="font-weight-bold">Código escaneado:</div>
            <div class="text-mono mt-1">{{ lastScannedCode }}</div>
            <div v-if="lastExtractedGTIN !== lastScannedCode" class="mt-2">
              <div class="font-weight-bold">GTIN extraído:</div>
              <div class="text-mono">{{ lastExtractedGTIN }}</div>
            </div>
          </v-alert>
          <p class="text-body-1">
            No se encontró un producto registrado con este código de barras. 
            ¿Deseas crear un nuevo producto?
          </p>
          <v-alert type="info" variant="text" class="mt-3" density="compact">
            <v-icon class="mr-2">mdi-information</v-icon>
            Se abrirá el formulario para crear un producto nuevo y luego se agregará automáticamente a este ingreso.
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn 
            color="grey" 
            variant="text" 
            @click="cancelCreateProduct"
          >
            Cancelar
          </v-btn>
          <v-btn 
            color="primary" 
            @click="confirmCreateProduct"
            prepend-icon="mdi-plus"
          >
            Crear Producto
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Modal de creación de nuevo producto -->
    <ProductDialog
      v-model="showNewProductDialog"
      :product="newProductData"
      mode="add"
      @save="saveNewProduct"
    />

  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import ZoneCapacityManager from '@/components/ZoneCapacityManager.vue'
import ResponsiveDataTable from '@/components/ResponsiveDataTable.vue'
import ProductDialog from '@/components/inventory/ProductDialog.vue'
import { BrowserMultiFormatReader } from '@zxing/library'
import JsBarcode from 'jsbarcode'
import api from '@/api'

export default {
  name: 'Ingresos',
  components: {
    ZoneCapacityManager,
    ResponsiveDataTable,
    ProductDialog
  },
  data() {
    return {
      filters: {
        status: '',
        dateFrom: '',
        dateTo: '',
        search: ''
      },
      isEditingIngreso: false,
      ingresoForm: {
        id: null,
        reference: 'trazalogis',                // ← nueva
        type: 'reception',            // ← fijo para ingresos
        status: 'pending',            // ← inicial
        supplier: '',                 // si lo usas
        reception_date: new Date().toISOString().split('T')[0],           // (lo ignorará el backend)
        destination_location_id: '',
        notes: '',
        items: [
          { product_id: null, quantity: 1, lot_number: '' }
        ]
      },
      selectedIngreso: null,
      selectedZone: null,
      hasCapacity: true,
      totalQuantity: 0,
      showDetailsModal: false,
      showDeleteModal: false,
      showCreateModal: false,
      newIngreso: {
        order_number: '',
        supplier: '',
        warehouse_id: '',
        zone_id: '',
        notes: '',
        items: [
          { 
            product_id: null, 
            quantity: 1, 
            lot_number: '', 
            expiry_date: '', 
            serial_number: '', 
            productSearch: '',
            // Campos de dispositivos médicos
            forma_adquisicion: '',
            fecha_recepcion_conforme: '',
            numero_registro_sanitario: '',
            numero_inventario_activo_fijo: ''
          }
        ]
      },
      submitting: false,
      showBarcodeScanner: false,
      codeReader: null,
      currentScanningIndex: null,
      // Variables para escaneo múltiple
      isMultipleScanMode: false,
      multipleScanInput: '',
      scannedProducts: [],
      lastScannedProduct: null,
      scanStatus: { type: '', message: '' },
      showEditModal: false,
      editingIngreso: {
        id: null,
        reference: '',
        supplier: '',
        status: '',
        notes: '',
        created_at: '',
        details: []
      },
      statusOptions: ['Pendiente', 'Completado', 'Cancelado'],
      showStorageConfigDialog: false,
      storageConfig: {
        pieceByPieceEnabled: false
      },
      // Almacenar productos filtrados por índice
      filteredProductsByIndex: {},
      filteredProductsByIndexEdit: {},
      // Variables para impresión de códigos de barras
      showPrintDialog: false,
      selectedProductForPrint: null,
      printing: false,
      printSettings: {
        printerType: 'standard',
        labelsPerRow: 2,
        labelSize: 'medium',
        labelFormat: '4x2',
        dpi: 203,
        copies: 1
      },
      
      // Variables para creación de productos on-the-fly
      showCreateProductConfirm: false,
      showNewProductDialog: false,
      lastScannedCode: '',
      lastExtractedGTIN: '',
      lastBarcodeAnalysis: null,
      newProductData: {
        sku: '',
        barcode: '',
        codigo_fabrica: '',
        name: '',
        description: '',
        category: '',
        abc_category: '',
        warehouse_id: null,
        zone_id: null,
        location_id: null,
        current_stock: 0,
        min_stock: 0,
        max_stock: 0,
        weight: 0,
        length: 0,
        width: 0,
        height: 0,
        status: 'Activo'
      },
      
      // Variables para flujo simplificado (directo al inventario)
      simplifiedMode: true, // Por defecto usar modo simplificado
      
      // ========== PARSER GS1 PROFESIONAL - DATOS ==========
      
      // Separador FNC1 (ASCII 29)
      GS: String.fromCharCode(29),
      
      // Definición de AIs (Application Identifiers) según especificaciones GS1
      AI_DEFS: [
        // ---- Longitud fija ----
        { key: "00",  len: 18, fixed: true,  name: "SSCC" },
        { key: "01",  len: 14, fixed: true,  name: "GTIN" }, // Identificador del producto
        { key: "02",  len: 14, fixed: true,  name: "Content GTIN" },
        { key: "11",  len: 6,  fixed: true,  name: "Prod date (YYMMDD)" }, // Fecha de producción
        { key: "13",  len: 6,  fixed: true,  name: "Pack date (YYMMDD)" }, // Fecha de empaquetado
        { key: "15",  len: 6,  fixed: true,  name: "Best before (YYMMDD)" }, // Fecha de consumo preferente
        { key: "17",  len: 6,  fixed: true,  name: "Expiry (YYMMDD)" }, // Fecha de vencimiento
        { key: "310", len: 6,  fixed: true,  name: "Net weight (kg, 6d)",  wildcard: true }, // 310x
        { key: "320", len: 6,  fixed: true,  name: "Net weight (lb, 6d)", wildcard: true }, // 320x
        { key: "330", len: 6,  fixed: true,  name: "Gross weight (kg, 6d)", wildcard: true }, // 330x - GS1 REQUERIDO
        { key: "340", len: 6,  fixed: true,  name: "Gross weight (lb, 6d)", wildcard: true }, // 340x - GS1 REQUERIDO
        { key: "390", len: null, fixed: false, name: "Amount w/o curr",   wildcard: true, max: 15 }, // 390x var
        { key: "414", len: 13, fixed: true,  name: "GLN" }, // Global Location Number - GS1 REQUERIDO
        
        // ---- Longitud variable ----
        { key: "10", fixed: false, name: "Batch/Lot", max: 20 }, // Lote
        { key: "21", fixed: false, name: "Serial",    max: 20 }, // Número de serie
        { key: "22", fixed: false, name: "CPV",       max: 20 }, // Consumer Product Variant
        { key: "30",  fixed: false, name: "Qty variable measure", max: 8 },
        { key: "37",  fixed: false, name: "Count", max: 8 },
        { key: "90",  fixed: false, name: "Internal use 90", max: 30 }, // GS1 REQUERIDO
        { key: "91",  fixed: false, name: "Internal use 91", max: 30 }, // GS1 REQUERIDO
        { key: "92",  fixed: false, name: "Internal use 92", max: 30 }, // GS1 REQUERIDO  
        { key: "93",  fixed: false, name: "Internal use 93", max: 30 }, // GS1 REQUERIDO
        { key: "94",  fixed: false, name: "Internal use 94", max: 30 }, // GS1 REQUERIDO
        { key: "95",  fixed: false, name: "Internal use 95", max: 30 }, // GS1 REQUERIDO
        { key: "96",  fixed: false, name: "Internal use 96", max: 30 }, // GS1 REQUERIDO
        { key: "97",  fixed: false, name: "Internal use 97", max: 30 }, // GS1 REQUERIDO
        { key: "98",  fixed: false, name: "Internal use 98", max: 30 }, // GS1 REQUERIDO
        { key: "99",  fixed: false, name: "Internal use 99", max: 30 }, // GS1 REQUERIDO
        { key: "240", fixed: false, name: "Additional ID", max: 30 },
        { key: "241", fixed: false, name: "Customer Part #", max: 30 },
        { key: "242", fixed: false, name: "MTO variant", max: 6 },
        { key: "243", fixed: false, name: "Supplier Part #", max: 30 },
        { key: "253", fixed: false, name: "GDTI + Serial", max: 30 }, // Global Document Type Identifier - GS1 REQUERIDO
        { key: "402", len: 17, fixed: true, name: "GSIN" }, // Global Shipment Identification Number - GS1 REQUERIDO
      ]
    }
  },
  computed: {
    ...mapGetters({
      ingresos: 'transactions/allIngresos',
      products: 'products/allProducts',
      zones: 'zones/allZones',
      loading: 'transactions/loading',
      warehouses: 'warehouses/allWarehouses'
    }),
    filteredIngresos() {
      if (!this.ingresos || !Array.isArray(this.ingresos)) {
        return [];
      }
      
      let result = this.ingresos
      
      if (this.filters.status) {
        result = result.filter(ingreso => ingreso.status === this.filters.status)
      }
      
      if (this.filters.dateFrom) {
        const fromDate = new Date(this.filters.dateFrom)
        result = result.filter(ingreso => {
          const ingresoDate = new Date(ingreso.created_at)
          return ingresoDate >= fromDate
        })
      }
      
      if (this.filters.dateTo) {
        const toDate = new Date(this.filters.dateTo)
        toDate.setHours(23, 59, 59, 999) // Final del día
        result = result.filter(ingreso => {
          const ingresoDate = new Date(ingreso.created_at)
          return ingresoDate <= toDate
        })
      }
      
      if (this.filters.search) {
        const search = this.filters.search.toLowerCase()
        result = result.filter(ingreso => {
          return (
            (ingreso.reference && ingreso.reference.toLowerCase().includes(search)) ||
            (ingreso.supplier && ingreso.supplier.toLowerCase().includes(search))
          )
        })
      }
      
      return result
    },
    isIngresoFormValid() {
      return (
        this.ingresoForm.items.length > 0 &&
        this.ingresoForm.items.every(item => item.product_id && item.quantity > 0)
      )
    },
    filteredZones() {
      if (!this.newIngreso.warehouse_id) return []
      return this.zones.filter(zone => zone.warehouse_id === this.newIngreso.warehouse_id)
    },
    availableZones() {
      return this.zones.filter(zone => zone.capacity > 0)
    },
    
    // Opciones para dispositivos médicos
    formasAdquisicion() {
      return [
        { title: 'Trato directo', value: 'Trato directo' },
        { title: 'Licitación pública', value: 'Licitación pública' },
        { title: 'Donación', value: 'Donación' },
        { title: 'CENABAST', value: 'CENABAST' },
        { title: 'Ley de Compras', value: 'Ley de Compras' },
        { title: 'Otro', value: 'Otro' }
      ]
    },
    
    // Helper para verificar si un item es válido
    isValidItem(item) {
      return item && typeof item === 'object' && 'product_id' in item
    },

    // Garantizar que siempre haya items válidos para renderizar
    safeNewIngresoItems() {
      if (!this.newIngreso || !this.newIngreso.items || !Array.isArray(this.newIngreso.items) || this.newIngreso.items.length === 0) {
        return []
      }
      return this.newIngreso.items
    },

    // Garantizar que siempre haya items válidos para el modal de edición
    safeEditIngresoItems() {
      if (!this.editingIngreso || !this.editingIngreso.details || !Array.isArray(this.editingIngreso.details) || this.editingIngreso.details.length === 0) {
        return []
      }
      return this.editingIngreso.details
    },
    
    // Validar que todos los campos requeridos de Dispositivos Médicos estén completos
    areDMFieldsComplete() {
      if (!this.newIngreso || !this.newIngreso.items || !Array.isArray(this.newIngreso.items)) {
        return true // Si no hay items, no hay nada que validar
      }
      
      // Verificar cada item que sea un DM priorizado
      for (const item of this.newIngreso.items) {
        if (!item.product_id) {
          continue // Saltar items sin producto seleccionado
        }
        
        // Verificar si es un DM priorizado
        if (this.isProductDMPriorizado(item.product_id)) {
          // Validar campos requeridos obligatorios para todos los DM
          if (!item.forma_adquisicion || !item.forma_adquisicion.trim()) {
            return false
          }
          
          if (!item.fecha_recepcion_conforme || !item.fecha_recepcion_conforme.trim()) {
            return false
          }
          
          // Validar registro sanitario si el producto lo requiere
          if (this.getProductRequiereRegistroSanitario(item.product_id)) {
            if (!item.numero_registro_sanitario || !item.numero_registro_sanitario.trim()) {
              return false
            }
          }
        }
      }
      
      return true // Todos los campos requeridos están completos
    },
    
    // Validación completa para habilitar el botón de crear ingreso
    canCreateIngreso() {
      return this.hasCapacity && this.areDMFieldsComplete
    }
  },
  methods: {
    ...mapActions({
      fetchIngresos: 'transactions/fetchIngresos',
      fetchProducts: 'products/fetchProducts',
      fetchLocations: 'zones/fetchZones',
      fetchZones: 'zones/fetchZones',
      fetchWarehouses: 'warehouses/fetchWarehouses',
      createTransaction: 'transactions/createTransaction',
      updateTransaction: 'transactions/updateTransaction',
      deleteTransaction: 'transactions/deleteTransaction',
      checkZoneCapacityAction: 'zones/checkZoneCapacity'
    }),

    // Helper para asegurar que un item tenga todos los campos necesarios
    ensureCompleteItem(item = {}) {
      return {
        product_id: item.product_id || null,
        quantity: item.quantity || 1,
        lot_number: item.lot_number || '',
        expiry_date: item.expiry_date || '',
        serial_number: item.serial_number || '',
        productSearch: item.productSearch || '',
        forma_adquisicion: item.forma_adquisicion || '',
        fecha_recepcion_conforme: item.fecha_recepcion_conforme || '',
        numero_registro_sanitario: item.numero_registro_sanitario || '',
        numero_inventario_activo_fijo: item.numero_inventario_activo_fijo || '',
        auto_filled: item.auto_filled || false,
        barcode_type: item.barcode_type || null,
        ...item // Preservar cualquier otra propiedad existente
      }
    },
    formatDate(dateString) {
      if (!dateString) return 'N/A'
      const date = new Date(dateString)
      return date.toLocaleDateString('es-ES')
    },
    getProductName(productId) {
      const product = this.products.find(p => p.id === productId)
      return product ? product.name : `Producto #${productId}`
    },
    getLocationName(locationId) {
      const location = this.locations.find(l => l.id === locationId)
      if (!location) return `Ubicación #${locationId}`
      
      const zone = this.zones.find(z => z.id === location.zone_id)
      if (zone) {
        return `${zone.name} (${zone.aisle}-${zone.rack}-${zone.level}-${zone.position})`
      }
      
      return location.code
    },
    getStatusClass(status) {
      const classes = {
        'Completado': 'bg-success',
        'Pendiente': 'bg-warning',
        'Cancelado': 'bg-danger'
      }
      return classes[status] || 'bg-secondary'
    },
    applyFilters() {
      // Los filtros se aplican automáticamente a través del computed property
    },
    async showNewIngresoModal() {
      this.isEditingIngreso = false
      
      // Verificar si los productos tienen el campo cantidad_x_caja
      // Si no lo tienen, recargar desde el backend
      if (this.products.length > 0 && !this.products[0].hasOwnProperty('cantidad_x_caja')) {
        console.log('⚠️ Productos sin campo cantidad_x_caja, recargando desde backend...')
        await this.$store.dispatch('products/fetchProducts')
      }
      
      // Inicializar datos ANTES de abrir el modal (síncrono)
      this.newIngreso = {
        order_number: '',
        supplier: '',
        warehouse_id: '',
        zone_id: '',
        notes: '',
        items: [this.ensureCompleteItem()]
      }
      
      this.resetIngresoForm()
      this.selectedZone = null
      this.hasCapacity = true
      this.filteredProductsByIndex = {}
      
      // Abrir modal DESPUÉS de inicializar los datos
      this.showCreateModal = true
    },
    editIngreso(ingreso) {
      this.isEditingIngreso = true
      
      this.editingIngreso = {
        id: ingreso.id,
        reference: ingreso.reference,
        supplier: ingreso.supplier || '',
        status: ingreso.status,
        notes: ingreso.notes || '',
        created_at: new Date(ingreso.created_at).toISOString().split('T')[0],
        details: (ingreso.details || []).map(detail => ({
          id: detail.id,
          product_id: detail.product ? detail.product.id : detail.product_id,
          quantity: detail.quantity,
          lot_number: detail.lot_number || '',
          expiry_date: detail.expiry_date ? detail.expiry_date.split('T')[0] : '',
          serial_number: detail.serial_number || '',
          product: detail.product,
          productSearch: ''
        }))
      }
      
      this.filteredProductsByIndexEdit = {}
      this.showEditModal = true
    },
    viewIngreso(ingreso) {
      this.selectedIngreso = ingreso
      this.showDetailsModal = true
    },
    deleteIngreso(ingreso) {
      this.selectedIngreso = ingreso
      this.showDeleteModal = true
    },
    confirmDeleteIngreso() {
      if (this.selectedIngreso) {
        this.submitting = true
        this.deleteTransaction(this.selectedIngreso.id)
          .then(() => {
            this.showDeleteModal = false
            this.fetchIngresos()
            this.$store.dispatch('showSuccess', 'Ingreso eliminado correctamente')
          })
          .catch(err => {
            console.error('Error al eliminar ingreso:', err)
            this.$store.dispatch('showError', 'Error al eliminar ingreso: ' + (err.message || 'Error desconocido'))
          })
          .finally(() => {
            this.submitting = false
          })
      }
    },
    addProductItem() {
      this.ingresoForm.items.push({
        product_id: '',
        quantity: 1,
        lot_number: ''
      })
    },
    removeProductItem(index) {
      this.ingresoForm.items.splice(index, 1)
      this.checkZoneCapacity()
    },
    resetIngresoForm() {
      this.ingresoForm = {
        id: null,
        supplier: '',
        reception_date: new Date().toISOString().split('T')[0],
        destination_location_id: '',
        notes: '',
        items: [{
          product_id: '',
          quantity: 1,
          lot_number: ''
        }]
      }
      
      // También resetear el formulario de nuevo ingreso
      this.newIngreso = {
        order_number: '',
        supplier: '',
        warehouse_id: '',
        zone_id: '',
        notes: '',
        items: [this.ensureCompleteItem()]
      }

      // Resetear modo escaneo múltiple
      this.isMultipleScanMode = false
      this.multipleScanInput = ''
      this.scannedProducts = []
      this.lastScannedProduct = null
      this.scanStatus = { type: '', message: '' }
    },
    saveIngreso() {
      return new Promise((resolve, reject) => {
        // Validar que todos los campos requeridos estén presentes
        if (!this.newIngreso.order_number) {
          reject(new Error('El número de orden es requerido'))
          return
        }

        if (!this.newIngreso.zone_id) {
          reject(new Error('La zona es requerida'))
          return
        }

        if (!this.newIngreso.items.length) {
          reject(new Error('Debe agregar al menos un producto'))
          return
        }

        // Validar que todos los items tengan los campos requeridos
        const invalidItem = this.newIngreso.items.find(
          item => !item.product_id || !item.quantity
        )
        if (invalidItem) {
          reject(new Error('Todos los productos deben tener ID y cantidad'))
          return
        }

        const ingresoData = {
          reference: this.newIngreso.order_number,
          supplier: this.newIngreso.supplier,
          notes: this.newIngreso.notes,
          items: this.newIngreso.items.map(item => ({
            product_id: item.product_id,
            quantity: item.quantity,
            lot_number: item.lot_number,
            expiry_date: item.expiry_date,
            serial_number: item.serial_number || null,
            destination_location_id: this.newIngreso.zone_id,
            // Campos de dispositivos médicos (se incluyen solo si el producto es DM priorizado)
            forma_adquisicion: this.isProductDMPriorizado(item.product_id) ? item.forma_adquisicion : null,
            fecha_recepcion_conforme: this.isProductDMPriorizado(item.product_id) ? item.fecha_recepcion_conforme : null,
            numero_registro_sanitario: this.isProductDMPriorizado(item.product_id) ? item.numero_registro_sanitario : null,
            numero_inventario_activo_fijo: this.isProductDMPriorizado(item.product_id) ? item.numero_inventario_activo_fijo : null
          }))
        }
        
        console.log('📋 Datos del ingreso preparados:', ingresoData)
        console.log('🔄 Modo simplificado:', this.simplifiedMode ? 'SÍ (Directo al inventario)' : 'NO (Con asignación de almacenamiento)')
        
        if (this.simplifiedMode) {
          // Usar endpoint simplificado (directo al inventario)
          this.createSimplifiedReception(ingresoData)
            .then((result) => {
              console.log('✅ Recepción simplificada completada:', result)
              resolve(result)
            })
            .catch(err => {
              console.error('❌ Error en recepción simplificada:', err)
              reject(err)
            })
        } else {
          // Usar flujo tradicional (con asignación de almacenamiento)
          const traditionalData = {
            reference: ingresoData.reference,
            type: 'RECEPTION',
            status: 'Pendiente',
            supplier: ingresoData.supplier,
            notes: ingresoData.notes,
            details: ingresoData.items.map(item => ({
              product_id: item.product_id,
              quantity: item.quantity,
              lot_number: item.lot_number,
              expiry_date: item.expiry_date,
              serial_number: item.serial_number,
              destination_location_id: item.destination_location_id,
              // Campos de dispositivos médicos ya procesados en ingresoData
              forma_adquisicion: item.forma_adquisicion,
              fecha_recepcion_conforme: item.fecha_recepcion_conforme,
              numero_registro_sanitario: item.numero_registro_sanitario,
              numero_inventario_activo_fijo: item.numero_inventario_activo_fijo
            }))
          }
          
          this.createTransaction(traditionalData)
            .then(() => {
              resolve()
            })
            .catch(err => {
              reject(err)
            })
        }
      })
    },
    checkZoneCapacity() {
      if (!this.ingresoForm.destination_location_id) {
        this.selectedZone = null
        this.hasCapacity = true
        return
      }
      
      // Buscar la zona seleccionada
      const zoneId = this.ingresoForm.destination_location_id
      this.selectedZone = this.zones.find(z => z.id === zoneId)
      
      if (!this.selectedZone) {
        this.hasCapacity = true
        return
      }
      
      // Calcular la cantidad total de productos
      this.totalQuantity = this.ingresoForm.items.reduce((sum, item) => sum + (item.quantity || 0), 0)
      
      // Verificar si hay capacidad disponible
      if (this.selectedZone) {
        const projectedUsage = this.selectedZone.current_usage + this.totalQuantity
        this.hasCapacity = projectedUsage <= this.selectedZone.capacity
      }
    },
    
    // Método para crear recepción simplificada
    createSimplifiedReception(ingresoData) {
      console.log('🚀 Iniciando creación simplificada:', ingresoData)
      return this.$store.dispatch('transactions/createSimplifiedReception', ingresoData)
    },
    handleCapacityStatus(status) {
      this.hasCapacity = status.hasCapacity
    },
    getStatusColor(status) {
      const colors = {
        'Completado': 'success',
        'Pendiente': 'warning',
        'Cancelado': 'error'
      }
      return colors[status] || 'secondary'
    },
    getStatusText(status) {
      const texts = {
        'Completado': 'Completado',
        'Pendiente': 'Pendiente',
        'Cancelado': 'Cancelado'
      }
      return texts[status] || status
    },
    submitIngreso() {
      if (!this.$refs.createForm.validate()) {
        return
      }

      this.submitting = true
      this.saveIngreso()
        .then(() => {
          this.showCreateModal = false
          this.resetIngresoForm()
          this.fetchIngresos()
          this.$store.dispatch('showSuccess', 'Ingreso creado correctamente')
        })
        .catch(err => {
          console.error('Error al crear ingreso:', err)
          let errorMessage = 'Error desconocido'
          
          if (err) {
            if (err.response && err.response.data && err.response.data.error) {
              errorMessage = err.response.data.error
            } else if (err.message) {
              errorMessage = err.message
            } else if (typeof err === 'string') {
              errorMessage = err
            }
          }
          
          this.$store.dispatch('showError', `Error al crear ingreso: ${errorMessage}`)
        })
        .finally(() => {
          this.submitting = false
        })
    },
    addItem() {
      this.newIngreso.items.push(this.ensureCompleteItem())
    },
    removeItem(index) {
      this.newIngreso.items.splice(index, 1)
      this.checkZoneCapacity()
    },
    loadZones() {
      // Reset zone selection when warehouse changes
      this.newIngreso.zone_id = null
    },
    async startScan(itemIndex) {
      this.currentScanningIndex = itemIndex
      this.showBarcodeScanner = true
      
      try {
        this.codeReader = new BrowserMultiFormatReader()
        
        // Verificar si hay cámaras disponibles (como en la versión que funcionaba)
        const videoInputDevices = await this.codeReader.listVideoInputDevices()
        
        if (videoInputDevices.length === 0) {
          throw new Error('No se encontraron cámaras disponibles')
        }

        // Usar directamente decodeFromVideoDevice sin especificar dispositivo
        await this.codeReader.decodeFromVideoDevice(
          undefined, // Usar dispositivo por defecto
          this.$refs.videoElement,
          (result) => {
            if (result) {
              this.handleBarcodeScan(result.text)
            }
          }
        )
      } catch (error) {
        console.error('Error initializing barcode scanner:', error)
        
        // Manejar diferentes tipos de errores
        let errorMessage = 'Error al inicializar el escáner'
        if (error.name === 'NotAllowedError') {
          errorMessage = 'Permisos de cámara denegados. Por favor, permite el acceso a la cámara.'
        } else if (error.name === 'NotFoundError') {
          errorMessage = 'No se encontró ninguna cámara en el dispositivo.'
        } else if (error.name === 'NotSupportedError') {
          errorMessage = 'El navegador no soporta acceso a la cámara.'
        } else if (error.name === 'NotReadableError') {
          errorMessage = 'La cámara está siendo usada por otra aplicación.'
        } else if (error.message) {
          errorMessage = error.message
        }
        
        this.$store.dispatch('showError', errorMessage)
        this.showBarcodeScanner = false
      }
    },
    stopScanner() {
      try {
        // Detener el lector de códigos de barras
        if (this.codeReader) {
          this.codeReader.reset()
        }
        
        // Detener el stream de video
        if (this.$refs.videoElement && this.$refs.videoElement.srcObject) {
          const stream = this.$refs.videoElement.srcObject
          const tracks = stream.getTracks()
          tracks.forEach(track => track.stop())
          this.$refs.videoElement.srcObject = null
        }
      } catch (error) {
        console.error('Error stopping scanner:', error)
      }
      
      this.showBarcodeScanner = false
      this.currentScanningIndex = null
    },
    async handleBarcodeScan(barcode) {
      try {
        // Analizar código de barras para extraer GTIN si es GS1
        const barcodeAnalysis = this.analyzeBarcode(barcode)
        const searchBarcode = barcodeAnalysis.gtin || barcode
        
        try {
          const product = await this.$store.dispatch('products/findProductByBarcode', searchBarcode)
          
          if (product && this.currentScanningIndex !== null) {
            // Producto encontrado - asignarlo al índice actual
            this.newIngreso.items[this.currentScanningIndex].product_id = product.id
            this.stopScanner()
          }
          
        } catch (findError) {
          // Manejar error de producto no encontrado
          if (findError.message === 'Producto no encontrado' && this.currentScanningIndex !== null) {
            // Producto no encontrado - mostrar modal de confirmación
            this.stopScanner()
            
            // Guardar información para el modal
            this.lastScannedCode = barcode
            this.lastExtractedGTIN = searchBarcode
            this.lastBarcodeAnalysis = barcodeAnalysis
            
            console.log('🔍 Producto no encontrado en escaneo simple. Mostrando modal:', {
              originalCode: barcode,
              searchedBarcode: searchBarcode,
              scanningIndex: this.currentScanningIndex
            })
            
            // Mostrar modal de confirmación
            this.showCreateProductConfirm = true
          } else {
            // Error real o contexto no válido
            this.stopScanner()
            this.$store.dispatch('showError', findError.message || 'Error al buscar el producto')
          }
        }
        
      } catch (error) {
        console.error('Error procesando escaneo:', error)
        this.stopScanner()
        this.$store.dispatch('showError', 'Error al procesar el código escaneado')
      }
    },
    getFilteredProducts(index) {
      if (this.filteredProductsByIndex[index]) {
        return this.filteredProductsByIndex[index]
      }
      return this.products
    },
    handleProductSearch(search, index) {
      // Asegurar que el array de items existe y tiene el índice
      if (!this.newIngreso.items || !this.newIngreso.items[index]) {
        if (this.newIngreso && this.newIngreso.items) {
          // Crear item si no existe
          this.$set(this.newIngreso.items, index, this.ensureCompleteItem())
        }
        return
      }
      
      // Asegurar que el item tenga todas las propiedades necesarias
      this.newIngreso.items[index] = this.ensureCompleteItem(this.newIngreso.items[index])
      
      // Actualizar el texto de búsqueda
      this.newIngreso.items[index].productSearch = search
      
      if (!search || search.length < 2) {
        // Si la búsqueda es muy corta, usar la lista completa de productos
        delete this.filteredProductsByIndex[index]
        return
      }

      // Filtrar productos localmente por SKU o nombre
      const searchLower = search.toLowerCase()
      const filteredProducts = this.products.filter(product => 
        product.sku.toLowerCase().includes(searchLower) ||
        product.name.toLowerCase().includes(searchLower)
      )
      
      // Actualizar la lista de productos filtrados para este item
      this.filteredProductsByIndex[index] = filteredProducts
      
      // Si se encontró exactamente un producto y la búsqueda es específica, seleccionarlo automáticamente
      if (filteredProducts.length === 1 && search.length >= 4) {
        this.newIngreso.items[index].product_id = filteredProducts[0].id
      }
    },
    handleProductSelection(index, productId) {
      if (productId) {
        this.newIngreso.items[index].product_id = productId
        this.checkZoneCapacity()
      }
    },
    handleQuantityChange(index) {
      // Si la cantidad no es 1, limpiar el serial number
      if (this.newIngreso.items[index].quantity !== 1) {
        this.newIngreso.items[index].serial_number = ''
      }
    },
    handleQuantityChangeEdit(index) {
      // Si la cantidad no es 1, limpiar el serial number en edición
      if (this.editingIngreso.details[index].quantity !== 1) {
        this.editingIngreso.details[index].serial_number = ''
      }
    },
    getFilteredProductsEdit(index) {
      if (this.filteredProductsByIndexEdit[index]) {
        return this.filteredProductsByIndexEdit[index]
      }
      return this.products
    },
    handleProductSearchEdit(search, index) {
      // Actualizar el texto de búsqueda
      this.editingIngreso.details[index].productSearch = search
      
      if (!search || search.length < 2) {
        // Si la búsqueda es muy corta, usar la lista completa de productos
        delete this.filteredProductsByIndexEdit[index]
        return
      }

      // Filtrar productos localmente por SKU o nombre
      const searchLower = search.toLowerCase()
      const filteredProducts = this.products.filter(product => 
        product.sku.toLowerCase().includes(searchLower) ||
        product.name.toLowerCase().includes(searchLower)
      )
      
      // Actualizar la lista de productos filtrados para este item
      this.filteredProductsByIndexEdit[index] = filteredProducts
      
      // Si se encontró exactamente un producto y la búsqueda es específica, seleccionarlo automáticamente
      if (filteredProducts.length === 1 && search.length >= 4) {
        this.editingIngreso.details[index].product_id = filteredProducts[0].id
      }
    },
    handleProductSelectionEdit(index, productId) {
      if (productId) {
        this.editingIngreso.details[index].product_id = productId
      }
    },
    saveEditedIngreso() {
      if (!this.$refs.editForm.validate()) {
        return
      }

      this.submitting = true
      
      const updateData = {
        reference: this.editingIngreso.reference,
        supplier: this.editingIngreso.supplier,
        status: this.editingIngreso.status,
        notes: this.editingIngreso.notes,
        details: this.editingIngreso.details.map(detail => ({
          product_id: detail.product_id,
          quantity: detail.quantity,
          lot_number: detail.lot_number,
          expiry_date: detail.expiry_date,
          status: detail.status || 'pending'
        }))
      }

      console.log('=== DEBUG SAVE EDITED INGRESO ===')
      console.log('editingIngreso.details:', this.editingIngreso.details)
      console.log('updateData:', updateData)
      console.log('updateData.details:', updateData.details)

      // Usar la acción del store para actualizar la transacción
      this.updateTransaction({ id: this.editingIngreso.id, data: updateData })
        .then(() => {
          this.showEditModal = false
          this.fetchIngresos()
          this.$store.dispatch('showSuccess', 'Ingreso actualizado correctamente')
        })
        .catch(err => {
          console.error('Error al actualizar ingreso:', err)
          this.$store.dispatch('showError', 'Error al actualizar ingreso: ' + (err.message || 'Error desconocido'))
        })
        .finally(() => {
          this.submitting = false
        })
    },
    async createStorageList(item) {
      // Verificar que la transacción esté en estado pendiente
      if (item.status !== 'Pendiente') {
        this.$store.dispatch('showError', 'Solo se pueden crear listas de almacenamiento para ingresos pendientes')
        return
      }

      // Resetear configuración y mostrar modal
      this.storageConfig.pieceByPieceEnabled = false
      this.selectedIngreso = item
      this.showStorageConfigDialog = true
    },
    async confirmCreateStorageList() {
      if (!this.selectedIngreso) return

      this.submitting = true

      try {
        // Llamar al API para crear la lista de almacenamiento
        const response = await api.createStorageList({
          transaction_id: this.selectedIngreso.id,
          storage_strategy: 'abc_optimized', // Estrategia por defecto
          piece_by_piece_enabled: this.storageConfig.pieceByPieceEnabled
        })

        const storageList = response.data

        this.$store.dispatch('showSuccess', 
          `Lista de almacenamiento ${storageList.list_number} creada exitosamente`
        )

        // Cerrar modal
        this.showStorageConfigDialog = false
        this.selectedIngreso = null

        // Actualizar la lista de ingresos
        await this.fetchIngresos()

        // Preguntar si desea ir a la vista de asignación de servicios
        const goToAssignment = confirm(
          '¿Desea ir a la vista de Asignación de Servicios para gestionar la lista creada?'
        )

        if (goToAssignment) {
          this.$router.push({
            path: '/service-assignment',
            query: { tab: 'storage' }
          })
        }

      } catch (error) {
        console.error('Error creating storage list:', error)
        let errorMessage = 'Error al crear la lista de almacenamiento'
        
        if (error.response?.data?.error) {
          errorMessage = error.response.data.error
        } else if (error.message) {
          errorMessage = error.message
        }
        
        this.$store.dispatch('showError', errorMessage)
      } finally {
        this.submitting = false
      }
    },
    // Funciones para el modal de editar
    addEditItem() {
      this.editingIngreso.details.push({
        product_id: null,
        quantity: 1,
        lot_number: '',
        expiry_date: '',
        serial_number: '',
        productSearch: ''
      })
    },
    removeEditItem(index) {
      this.editingIngreso.details.splice(index, 1)
    },
    async startScanEdit(itemIndex) {
      this.currentScanningIndex = itemIndex
      this.showBarcodeScanner = true
      
      try {
        this.codeReader = new BrowserMultiFormatReader()
        
        const videoInputDevices = await this.codeReader.listVideoInputDevices()
        
        if (videoInputDevices.length === 0) {
          throw new Error('No se encontraron cámaras disponibles')
        }

        await this.codeReader.decodeFromVideoDevice(
          undefined,
          this.$refs.videoElement,
          (result) => {
            if (result) {
              this.handleBarcodeScanEdit(result.text)
            }
          }
        )
      } catch (error) {
        console.error('Error initializing barcode scanner:', error)
        
        let errorMessage = 'Error al inicializar el escáner'
        if (error.name === 'NotAllowedError') {
          errorMessage = 'Permisos de cámara denegados. Por favor, permite el acceso a la cámara.'
        } else if (error.name === 'NotFoundError') {
          errorMessage = 'No se encontró ninguna cámara en el dispositivo.'
        } else if (error.name === 'NotSupportedError') {
          errorMessage = 'El navegador no soporta acceso a la cámara.'
        } else if (error.name === 'NotReadableError') {
          errorMessage = 'La cámara está siendo usada por otra aplicación.'
        } else if (error.message) {
          errorMessage = error.message
        }
        
        this.$store.dispatch('showError', errorMessage)
        this.showBarcodeScanner = false
      }
    },
    async handleBarcodeScanEdit(barcode) {
      try {
        const product = await this.$store.dispatch('products/findProductByBarcode', barcode)
        if (product && this.currentScanningIndex !== null) {
          this.editingIngreso.details[this.currentScanningIndex].product_id = product.id
          this.stopScanner()
        } else {
          this.$store.dispatch('showError', 'Producto no encontrado')
        }
      } catch (error) {
        console.error('Error finding product:', error)
        this.$store.dispatch('showError', 'Error al buscar el producto')
      }
    },

    closeCreateModal() {
      // Resetear modo escaneo múltiple
      this.isMultipleScanMode = false
      this.multipleScanInput = ''
      this.scannedProducts = []
      this.lastScannedProduct = null
      this.scanStatus = { type: '', message: '' }
      
      // Cerrar modal
      this.showCreateModal = false
    },

    // Métodos para escaneo múltiple
    toggleMultipleScanMode() {
      if (this.isMultipleScanMode) {
        // Finalizar modo escaneo
        this.isMultipleScanMode = false
        this.multipleScanInput = ''
        this.scannedProducts = []
        this.lastScannedProduct = null
        this.scanStatus = { type: '', message: '' }
      } else {
        // Activar modo escaneo
        this.isMultipleScanMode = true
        this.multipleScanInput = ''
        this.scannedProducts = []
        this.lastScannedProduct = null
        this.scanStatus = { type: '', message: '' }
        
        // Enfocar el input después de que se muestre
        this.$nextTick(() => {
          if (this.$refs.multipleScanInput) {
            this.$refs.multipleScanInput.focus()
          }
        })
      }
    },

    handleBarcodeInput(value) {
      // Limpiar mensajes de estado después de un breve delay
      if (this.scanStatus.message) {
        setTimeout(() => {
          this.scanStatus = { type: '', message: '' }
        }, 2000)
      }
    },

    // ========== MÉTODOS DE ANÁLISIS DE CÓDIGOS DE BARRAS ==========
    
    // Detectar tipo de código de barras y extraer GTIN si es GS1
    analyzeBarcode(barcode) {
      if (!barcode || barcode.trim() === '') {
        return { type: 'Sin código', gtin: null, original: barcode, variableData: null }
      }
      
      const cleaned = barcode.trim()
      
      console.log('🧪 Analizando código:', {
        original: cleaned,
        length: cleaned.length,
        startsWithAI01: cleaned.startsWith('01'),
        hasParens: cleaned.includes('(')
      })
      
      // Detección GS1 mejorada - según especificaciones del usuario
      const isGS1 = cleaned.includes('(') || 
                   (cleaned.startsWith('01') && cleaned.length > 16) ||
                   (cleaned.length > 20 && /^\d/.test(cleaned)) ||
                   (cleaned.includes(String.fromCharCode(29))) // Incluir códigos con separador FNC1
      
      if (isGS1) {
        const { gtin, variableData } = this.extractGTINAndVariableData(cleaned)
        const hasVariableData = variableData && Object.keys(variableData).length > 0
        
        console.log('✅ Identificado como GS1:', {
          gtin: gtin,
          hasVariableData: hasVariableData,
          variableData: variableData
        })
        
        return { 
          type: 'GS1', 
          gtin: gtin,
          original: cleaned,
          variableData: variableData,
          hasVariableData: hasVariableData
        }
      }
      
      // Solo evaluar códigos de longitud fija si NO es GS1
      // EAN-13 (exactamente 13 dígitos)
      if (/^\d{13}$/.test(cleaned)) {
        console.log('📊 Identificado como EAN-13:', cleaned)
        return { type: 'EAN-13', gtin: cleaned, original: cleaned, variableData: null }
      }
      
      // UPC-A (exactamente 12 dígitos)
      if (/^\d{12}$/.test(cleaned)) {
        console.log('📊 Identificado como UPC-A:', cleaned)
        return { type: 'UPC-A', gtin: cleaned, original: cleaned, variableData: null }
      }
      
      // EAN-8 (exactamente 8 dígitos)
      if (/^\d{8}$/.test(cleaned)) {
        console.log('📊 Identificado como EAN-8:', cleaned)
        return { type: 'EAN-8', gtin: cleaned, original: cleaned, variableData: null }
      }
      
      // Códigos de longitud intermedia que podrían ser variantes
      if (/^\d{10,11}$/.test(cleaned)) {
        console.log('📊 Identificado como UPC:', cleaned)
        return { type: 'UPC', gtin: cleaned, original: cleaned, variableData: null }
      }
      
      // Otro formato
      console.log('❓ Clasificado como Otro:', cleaned)
      return { type: 'Otro', gtin: cleaned, original: cleaned, variableData: null }
    },

    // Extraer GTIN y datos variables de código GS1
    extractGTINAndVariableData(gs1Code) {
      if (!gs1Code) return { gtin: null, variableData: null }
      
      let cleaned = gs1Code.replace(/[()]/g, '') // Remover paréntesis si existen
      cleaned = this.normalizeFNC1(cleaned)      // Normalizar FNC1 desde escáner DS2278
      let gtin = null
      let variableData = {}
      
      console.log('🔧 Extrayendo GTIN de:', cleaned)
      console.log('🔧 Código después de normalizar FNC1:', cleaned.replace(new RegExp(this.GS, 'g'), '<FNC1>'))
      
      // Log del código normalizado para debug
      if (window.debugGS1) {
        console.log('🚨 DEBUG GS1: Código detectado')
        console.log('Código completo:', cleaned)
        console.log('¿Contiene FNC1?', cleaned.includes(this.GS))
      }
      
      try {
        // Método mejorado según especificaciones del usuario:
        // "Si el código empieza con 01 los siguientes 14 caracteres son el GTIN"
        
        if (cleaned.startsWith('01') && cleaned.length >= 16) {
          // Extraer exactamente los siguientes 14 caracteres después de "01"
          gtin = cleaned.substring(2, 16) // Posiciones 2-15 (14 dígitos del GTIN)
          const remainingData = cleaned.substring(16) // Todo lo que viene después del GTIN
          
          console.log('✅ GTIN extraído según especificación (01 + 14 chars):', gtin)
          console.log('🔍 Datos restantes para parsing:', remainingData)
          
          if (remainingData && remainingData.length > 0) {
            // Parsear datos variables que vienen después del GTIN
            variableData = this.parseGS1VariableData(remainingData)
          }
        } else {
          // Métodos alternativos para otros casos
          const gtinMatch = cleaned.match(/^01(\d{14})(.*)/)
          if (gtinMatch) {
            gtin = gtinMatch[1] // GTIN de 14 dígitos
            const remainingData = gtinMatch[2] // Datos adicionales después del GTIN
            
            console.log('✅ GTIN extraído (regex):', gtin)
            console.log('🔍 Datos restantes para parsing:', remainingData)
            
            if (remainingData && remainingData.length > 0) {
              variableData = this.parseGS1VariableData(remainingData)
            }
          } else if (/^\d+$/.test(cleaned) && cleaned.length > 14) {
            // Si es un código largo sin AI visible, intentar extraer los primeros 14 dígitos
            gtin = cleaned.substring(0, 14)
            console.log('⚠️ GTIN extraído por longitud:', gtin)
          }
        }
        
        // Si no se puede extraer GTIN, usar el código original
        if (!gtin) {
          console.log('❌ No se pudo extraer GTIN, usando código completo')
          gtin = cleaned
        }
        
      } catch (error) {
        console.error('❌ Error parsing GS1 code:', error)
        gtin = cleaned
      }
      
      console.log('🎯 Resultado final - GTIN:', gtin, 'Variables:', variableData)
      return { gtin, variableData }
    },

    // ========== PARSER GS1 PROFESIONAL ==========
    
    // Función de utilidad para parsear fechas GS1 (YYMMDD → YYYY-MM-DD)
    parseGS1Date(dateValue, aiType = 'genérica') {
      if (!dateValue || dateValue.length !== 6) {
        console.warn(`⚠️ Fecha GS1 inválida (${aiType}):`, dateValue)
        return null
      }
      
      const yy = dateValue.substring(0, 2)
      const mm = dateValue.substring(2, 4)
      let dd = dateValue.substring(4, 6)
      
      // Validar rangos básicos
      const monthNum = parseInt(mm)
      const dayNum = parseInt(dd)
      
      if (monthNum < 1 || monthNum > 12) {
        console.warn(`⚠️ Mes inválido en fecha GS1 (${aiType}):`, dateValue, `→ mm=${mm}`)
        return null
      }
      
      // Corregir día 00 a 01 para fechas válidas
      if (dd === '00') {
        dd = '01'
        console.log(`📅 Corrigiendo día 00 → 01 en fecha ${aiType}: ${dateValue} → ${yy}${mm}${dd}`)
      }
      
      // Validar día después de corrección
      if (dayNum < 0 || dayNum > 31) {
        console.warn(`⚠️ Día inválido en fecha GS1 (${aiType}):`, dateValue, `→ dd=${dd}`)
        return null
      }
      
      const parsedDate = `20${yy}-${mm}-${dd}`
      console.log(`📅 Fecha GS1 parseada (${aiType}): ${dateValue} → ${parsedDate}`)
      return parsedDate
    },
    
    // Construir índices de búsqueda
    buildAIIndex() {
      const exact = new Map()
      const wild = []
      
      for (const d of this.AI_DEFS) {
        if (d.wildcard) {
          const re = new RegExp("^" + d.key + "\\d")
          wild.push({ re, def: d })
        } else {
          exact.set(d.key, d)
        }
      }
      
      return { exact, wild }
    },
    
    // Encontrar AI en posición específica
    findAI(s, pos, aiIndex) {
      const chunk4 = s.slice(pos, pos + 4)
      const chunk3 = s.slice(pos, pos + 3)
      const chunk2 = s.slice(pos, pos + 2)
      
      // Wildcards primero (310x, 320x, 390x, etc)
      for (const { re, def } of aiIndex.wild) {
        if (re.test(s.slice(pos, pos + 4))) {
          return { key: s.slice(pos, pos + 4), base: def.key, def }
        }
      }
      
      // Exactos 4 → 3 → 2 dígitos
      if (aiIndex.exact.has(chunk4)) return { key: chunk4, base: chunk4, def: aiIndex.exact.get(chunk4) }
      if (aiIndex.exact.has(chunk3)) return { key: chunk3, base: chunk3, def: aiIndex.exact.get(chunk3) }
      if (aiIndex.exact.has(chunk2)) return { key: chunk2, base: chunk2, def: aiIndex.exact.get(chunk2) }
      
      return null
    },
    
    // Validar si una secuencia AI es realmente válida - Simplificado con regla de AI único
    isValidAISequence(aiMatch, remainingData) {
      if (!aiMatch || !remainingData) return false
      
      const { def } = aiMatch
      
      console.log(`🔍 Validando AI ${aiMatch.key} con datos: "${remainingData.substring(0, 15)}..."`)
      
      // Para AIs de longitud fija, verificar que hay suficiente contenido
      if (def.fixed) {
        const requiredLength = def.len
        if (remainingData.length < requiredLength) {
          console.log(`❌ AI ${aiMatch.key} requiere ${requiredLength} chars, solo quedan ${remainingData.length}`)
          return false
        }
        
        // Para fechas, verificar formato básico (6 dígitos)
        if (['11', '13', '15', '17'].includes(def.key) && !/^\d{6}/.test(remainingData)) {
          console.log(`❌ AI fecha ${aiMatch.key} no tiene formato válido:`, remainingData.substring(0, 6))
          return false
        }
        
        console.log(`✅ AI fijo ${aiMatch.key} válido`)
        return true
      }
      
      // Para AIs de longitud variable
      if (remainingData.length === 0) {
        console.log(`❌ AI variable ${aiMatch.key} sin contenido`)
        return false
      }
      
      // Validación especial para AI (01) GTIN
      if (aiMatch.base === '01') {
        if (remainingData.length < 14 || !/^\d{14}/.test(remainingData)) {
          console.log(`❌ AI (01) debe tener 14 dígitos, encontrado:`, remainingData.substring(0, 14))
          return false
        }
        
        const afterGTIN = remainingData.substring(14)
        if (afterGTIN.length > 0) {
          const nextPossibleAI = afterGTIN.substring(0, 2)
          const validNextAIs = ['10', '11', '13', '15', '17', '21', '30', '37', '90', '91', '92', '93', '94', '95', '96', '97', '98', '99', '240', '241', '242', '243', '253', '310', '320', '330', '340', '390', '414', '402']
          
          if (!validNextAIs.includes(nextPossibleAI) && afterGTIN.length > 2) {
            console.log(`❌ AI (01) seguido de secuencia inválida: "${nextPossibleAI}"`)
            return false
          }
        }
      }
      
      // Para AI (10) lote, verificar contenido mínimo razonable
      if (aiMatch.base === '10') {
        if (remainingData.length <= 2) {
          console.log(`❌ AI (10) con contenido muy corto: "${remainingData}"`)
          return false
        }
      }
      
      console.log(`✅ AI variable ${aiMatch.key} válido`)
      return true
    },
    
    // Normalizar distintos formatos de FNC1 a un único \x1D
    normalizeFNC1(s) {
      if (!s) return s;
      
      if (window.debugGS1) {
        console.log('🔧 normalizeFNC1 input:', s);
      }
      
      let result = s
        .replace(/\u001D/g, this.GS)                   // FNC1 real (ASCII 29)
        .replace(/<FNC1>|<GS>|\|/gi, this.GS);         // si usaste texto visible
      
      // Parche mejorado: el lector está enviando "0029". 
      // Convertir SOLO cuando aparece como separador entre un valor y el siguiente AI válido
      const before = result;
      result = result.replace(/(\d{3,30})0029(?=(11|13|15|17|10|21|22|240|241|242|243|30|37|90|91|92|93|94|95|96|97|98|99|253|310|320|330|340|390|414|402))/g, `$1${this.GS}`);
      
      if (before !== result) {
        console.log('✅ Se aplicaron reemplazos de 0029:', {
          before: before,
          after: result.replace(new RegExp(this.GS, 'g'), '<FNC1>')
        });
      } else {
        console.log('❌ NO se aplicaron reemplazos de 0029');
        
        // Intentar método alternativo: buscar patrones específicos
        if (s.includes('0029')) {
          console.log('🔧 Intentando método alternativo para 0029...');
          // Buscar patrones más específicos
          const alternativePattern = /(\d{3,30})0029(\d{2,4})/g;
          let match;
          while ((match = alternativePattern.exec(s)) !== null) {
            const [full, beforeValue, afterValue] = match;
            console.log('📍 Patrón encontrado:', {
              full: full,
              before: beforeValue,
              after: afterValue,
              afterLength: afterValue.length
            });
            
            // Verificar si el "after" parece un AI válido
            const commonAIs = ['10', '11', '13', '15', '17', '21', '30', '37'];
            const possibleAI = afterValue.substring(0, 2);
            if (commonAIs.includes(possibleAI)) {
              console.log('✅ Detectado AI válido:', possibleAI);
              result = result.replace(full, `${beforeValue}${this.GS}${afterValue}`);
            }
          }
        }
      }
      
      console.log('🎯 normalizeFNC1 result:', result.replace(new RegExp(this.GS, 'g'), '<FNC1>'));
      return result;
    },

        // Parser GS1 completo con regla: cada AI aparece UNA SOLA VEZ
    parseGS1(raw) {
      if (!raw) return { items: [], warnings: ["Empty input"] }
      
      // Normalización mejorada para DS2278
      let s = String(raw).trim()
      s = s.replace(/^\](C1|e0|E0|d2|Q3)/i, "")   // quitar prefijos de simbología
      s = this.normalizeFNC1(s)                   // normalizar FNC1 desde escáner DS2278
      s = s.replace(/<FNC1>|<GS>/gi, this.GS)    // algunos lectores usan textos literales
      
      console.log('🔍 Parseando GS1 completo:', s.replace(new RegExp(this.GS, 'g'), '<FNC1>'))
      
      const aiIndex = this.buildAIIndex()
      const items = []
      const warnings = []
      const processedAIs = new Set() // Registro de AIs ya procesados
      let i = 0
      
      while (i < s.length) {
        // Saltar separadores FNC1
        if (s[i] === this.GS) { 
          i++
          console.log('⚡ FNC1 encontrado y saltado')
          continue 
        }
        
        const m = this.findAI(s, i, aiIndex)
        if (!m) {
          warnings.push(`AI desconocido en posición ${i}: "${s.slice(i, i+4)}..."`)
          console.log(`⚠️ AI desconocido en posición ${i}:`, s.slice(i, i+4))
          break
        }
        
        const { key, base, def } = m
        i += key.length
        
        // REGLA FUNDAMENTAL: Verificar si este AI ya fue procesado
        if (processedAIs.has(base)) {
          console.log(`⚠️ AI ${base} duplicado detectado - IGNORANDO (regla: un AI por código)`)
          warnings.push(`AI duplicado ${base} ignorado`)
          break // O continuar hasta el final del string como contenido del AI anterior
        }
        
        console.log(`📊 Procesando AI ${key} (${def.name}) - Primer encuentro ✓`)
        processedAIs.add(base) // Marcar como procesado
        
        let value = ""
        let ambiguous = false
        
        if (def.fixed) {
          // Longitud fija
          const need = def.len
          if (i + need > s.length) {
            value = s.slice(i)
            warnings.push(`Valor incompleto para (${key}) esperaba ${need} chars.`)
            i = s.length
          } else {
            value = s.slice(i, i + need)
            i += need
          }
                 } else {
          // Longitud variable: SOLO cortar con FNC1 real o límite máximo
           const max = def.max ?? 30
           let j = i
           let cutByFNC1 = false
           
           while (j < s.length && (j - i) < max) {
            // REGLA ESTRICTA: Solo cortar con FNC1 real
             if (s[j] === this.GS) { 
              console.log(`✂️ FNC1 encontrado, cortando en posición ${j}`)
               cutByFNC1 = true
               break 
             }
            j++
          }
          
          // Si no hay FNC1, tomar todo hasta el límite máximo o fin del string
          console.log(`📏 Campo variable sin FNC1: tomando hasta posición ${j} (${j-i} chars)`)
                 
          // OPCIONAL: Buscar AI solo si llegamos al límite máximo sin FNC1
          if ((j - i) >= max && j < s.length) {
            // Solo en este caso, buscar AI como posible separador
            for (let k = Math.max(i + 3, j - 10); k <= j + 2 && k < s.length - 1; k++) {
              const next = this.findAI(s, k, aiIndex)
              if (next && !processedAIs.has(next.base)) {
                const remainingAfterAI = s.slice(k + next.key.length)
                
                console.log(`🔍 AI potencial en límite máximo "${next.key}" en posición ${k}`)
                
                   if (this.isValidAISequence(next, remainingAfterAI)) {
                  console.log(`✂️ CORTANDO por límite máximo + AI válido: "${next.key}"`)
                  j = k
                     break
                   }
                 }
               }
           }
           
           value = s.slice(i, j)
           i = j
           if (s[i] === this.GS) i++ // Consumir FNC1 si lo había
           
          // Detectar ambigüedad solo si no fue cortado por FNC1
           if (!cutByFNC1) {
             const nxt = this.findAI(s, i, aiIndex)
            if (nxt && nxt.def && !nxt.def.fixed && !processedAIs.has(nxt.base)) {
              ambiguous = true
            }
           }
         }
        
        console.log(`✅ ${def.name} extraído: "${value}"${ambiguous ? ' (ambiguous)' : ''}`)
        items.push({ ai: key, aiBase: base, name: def.name, value, ambiguous })
      }
      
      console.log(`🎯 AIs procesados: ${Array.from(processedAIs).join(', ')}`)
      return { items, warnings }
    },
    
    // Parsear datos variables del GS1 - versión simplificada que usa el parser completo
    parseGS1VariableData(remainingData) {
      const variableData = {}
      
      console.log('🔍 Parseando datos GS1 con parser profesional:', remainingData)
      
      try {
        // Usar el parser completo
        const result = this.parseGS1(remainingData)
        
        if (result.warnings.length > 0) {
          console.warn('⚠️ Advertencias GS1:', result.warnings)
        }
        
        // Variables temporales para fechas
        let expiryDate = null
        let productionDate = null
        let packagingDate = null
        let bestBeforeDate = null
        
        // Convertir items a formato esperado por el resto del código
        for (const item of result.items) {
          switch (item.aiBase) {
            case '10':
              variableData.lot_number = item.value
              break
            case '11':
              // Fecha de producción YYMMDD → YYYY-MM-DD
              productionDate = this.parseGS1Date(item.value, 'producción')
              if (productionDate) {
                variableData.production_date = productionDate
              }
              break
            case '13':
              // Fecha de empaquetado YYMMDD → YYYY-MM-DD
              packagingDate = this.parseGS1Date(item.value, 'empaquetado')
              if (packagingDate) {
                variableData.packaging_date = packagingDate
              }
              break
            case '15':
              // Fecha de consumo preferente YYMMDD → YYYY-MM-DD
              bestBeforeDate = this.parseGS1Date(item.value, 'consumo preferente')
              if (bestBeforeDate) {
                variableData.best_before_date = bestBeforeDate
              }
              break
            case '17':
              // Fecha de vencimiento YYMMDD → YYYY-MM-DD
              expiryDate = this.parseGS1Date(item.value, 'vencimiento')
              if (expiryDate) {
                variableData.expiry_date = expiryDate
              }
              break
            case '21':
              variableData.serial_number = item.value
              break
            case '30':
              variableData.quantity = item.value
              break
            case '37':
              variableData.count = item.value
              break
          }
        }
        
        // Lógica de prioridad para fechas según especificaciones del usuario:
        // Si no hay fecha de vencimiento (17), usar otras fechas en el campo de vencimiento
        if (!expiryDate) {
          if (bestBeforeDate) {
            // Prioridad 1: Best before date (15)
            variableData.expiry_date = bestBeforeDate
            console.log('📅 Usando fecha de consumo preferente (15) como vencimiento:', bestBeforeDate)
          } else if (packagingDate) {
            // Prioridad 2: Packaging date (13) 
            variableData.expiry_date = packagingDate
            console.log('📅 Usando fecha de empaquetado (13) como vencimiento:', packagingDate)
          } else if (productionDate) {
            // Prioridad 3: Production date (11)
            variableData.expiry_date = productionDate
            console.log('📅 Usando fecha de producción (11) como vencimiento:', productionDate)
          }
        }
        
      } catch (error) {
        console.error('❌ Error parsing GS1 variable data:', error)
      }
      
      console.log('🎯 Datos variables extraídos:', variableData)
      return variableData
    },

    // ========== FUNCIÓN DE PRUEBA TEMPORAL ==========
    
    testGS1Parsing() {
      console.log('🧪 === INICIO PRUEBAS GS1 ===')
      
      // Códigos de prueba según especificaciones del usuario
      const testCodes = [
        // Con fecha de vencimiento (17)
        '01012345678901231722101210ABC123',
        // Con fecha de producción (11)  
        '01012345678901231122101021ABC123',
        // Con fecha de empaquetado (13)
        '01012345678901231322101321ABC123',
        // Con fecha de consumo preferente (15)
        '01012345678901231522101521ABC123',
              // Con día 00 (caso específico del usuario) - debe convertirse a 01
      '0101234567890123172812001012345',
      // Casos específicos de lotes problemáticos del usuario
      '05030267047666172611001012620', // Lote: 1212620 (funciona)
      '050302670476661727010010122154', // Lote: 1220154 (problema con 01)
      '050302670476802806001032312014', // Lote: 32312014 (problema con 014)
        // Con lote y serial
        '0101234567890123102022101221ABC12321SN123456',
        // Con separadores FNC1
        `01012345678901231722101210${String.fromCharCode(29)}21ABC123`,
        // Código simple EAN-13
        '7891234567890'
      ]
      
      testCodes.forEach((code, index) => {
        console.log(`\n🔍 Prueba ${index + 1}: ${code}`)
        const analysis = this.analyzeBarcode(code)
        console.log('Resultado:', analysis)
      })
      
      console.log('🧪 === FIN PRUEBAS GS1 ===\n')
      
      // Probar casos específicos de lotes problemáticos (solo si está habilitado)
      if (window.enableLotTests) {
        this.testLotParsing()
      }
      
      // Probar consolidación de productos idénticos
      this.testProductConsolidation()
    },
    
    // Método de prueba para consolidación de productos (solo desarrollo)
    testProductConsolidation() {
      console.log('🧪 === INICIO PRUEBAS CONSOLIDACIÓN ===')
      
      // Simular producto de prueba
      const mockProduct = {
        id: 999,
        name: 'Producto Test GS1',
        sku: 'TEST-GS1-001'
      }
      
      // Código GS1 idéntico (mismo GTIN, lote y fecha)
      const identicalCode = '01012345678901231722101210ABC123'
      
      console.log('📋 Estado inicial items:', this.newIngreso.items.length)
      
      // Simular escaneo del mismo código 3 veces
      for (let i = 1; i <= 3; i++) {
        console.log(`\n📱 Escaneo ${i}: ${identicalCode}`)
        const analysis = this.analyzeBarcode(identicalCode)
        console.log('Analysis:', analysis)
        
        this.addToMainProductListWithVariableData(mockProduct, analysis)
        
        console.log(`📋 Items después del escaneo ${i}:`, this.newIngreso.items.length)
        this.newIngreso.items.forEach((item, idx) => {
          if (item.product_id === mockProduct.id) {
            console.log(`   Item ${idx}: Cantidad=${item.quantity}, Lote=${item.lot_number}, Venc=${item.expiry_date}`)
          }
        })
      }
      
      // Verificar resultado
      const testItems = this.newIngreso.items.filter(item => item.product_id === mockProduct.id)
      if (testItems.length === 1 && testItems[0].quantity === 3) {
        console.log('✅ CONSOLIDACIÓN EXITOSA: 1 item con cantidad 3')
      } else {
        console.log('❌ CONSOLIDACIÓN FALLIDA:', testItems)
      }
      
      // Limpiar items de prueba
      this.newIngreso.items = this.newIngreso.items.filter(item => item.product_id !== mockProduct.id)
      
      console.log('🧪 === FIN PRUEBAS CONSOLIDACIÓN ===\n')
    },
    
    // ========== MÉTODOS PARA CREACIÓN DE PRODUCTOS ON-THE-FLY ==========
    
    // Cancelar creación de producto
    cancelCreateProduct() {
      this.showCreateProductConfirm = false
      this.lastScannedCode = ''
      this.lastExtractedGTIN = ''
      this.lastBarcodeAnalysis = null
      this.setScanStatus('ready', 'Escaneo cancelado. Listo para escanear.')
    },
    
    // Confirmar creación de producto
    confirmCreateProduct() {
      this.showCreateProductConfirm = false
      
      // Preparar datos para el nuevo producto
      this.prepareNewProductData()
      
      // Mostrar modal de creación
      this.showNewProductDialog = true
    },
    
    // Preparar datos del nuevo producto basados en el código escaneado
    prepareNewProductData() {
      const gtin = this.lastExtractedGTIN || this.lastScannedCode
      
      // Generar SKU sugerido basado en GTIN
      let suggestedSKU = ''
      if (gtin.length >= 8) {
        suggestedSKU = 'AUTO' + gtin.substring(0, 8).toUpperCase()
      } else {
        suggestedSKU = 'AUTO' + gtin.substring(0, Math.min(6, gtin.length)).toUpperCase()
      }
      
      // Preparar datos del producto
      this.newProductData = {
        sku: suggestedSKU,
        barcode: gtin, // Usar el GTIN extraído, no el código completo
        codigo_fabrica: '',
        name: '',
        description: '',
        category: '',
        abc_category: 'B', // Categoría por defecto
        warehouse_id: null,
        zone_id: null,
        location_id: null,
        current_stock: 0,
        min_stock: 0,
        max_stock: 0,
        weight: 0,
        length: 0,
        width: 0,
        height: 0,
        status: 'Activo'
      }
      
      console.log('📋 Producto preparado para creación:', {
        sku: this.newProductData.sku,
        barcode: this.newProductData.barcode,
        originalCode: this.lastScannedCode,
        analysis: this.lastBarcodeAnalysis?.type
      })
    },
    
    // Guardar nuevo producto y agregarlo al ingreso
    async saveNewProduct(productData) {
      try {
        this.setScanStatus('loading', 'Creando producto...')
        
        // Crear el producto usando el store
        const newProduct = await this.$store.dispatch('products/createProduct', productData)
        
        this.showNewProductDialog = false
        
        // Actualizar lista de productos
        await this.$store.dispatch('products/fetchProducts')
        
        this.setScanStatus('success', `Producto "${newProduct.name}" creado exitosamente`)
        
        // Agregar el producto recién creado al ingreso automáticamente
        setTimeout(() => {
          this.addNewlyCreatedProductToIngreso(newProduct, this.lastBarcodeAnalysis)
        }, 500)
        
      } catch (error) {
        console.error('Error al crear producto:', error)
        this.setScanStatus('error', 'Error al crear el producto: ' + (error.message || 'Error desconocido'))
        this.showNewProductDialog = false
      }
    },
    
    // Agregar producto recién creado al ingreso
    addNewlyCreatedProductToIngreso(product, barcodeAnalysis) {
      console.log('➕ Agregando producto recién creado al ingreso:', {
        product: product.name,
        id: product.id,
        hasVariableData: barcodeAnalysis?.hasVariableData,
        scanningIndex: this.currentScanningIndex
      })
      
      try {
        // Verificar si viene de escaneo simple (tiene currentScanningIndex) o múltiple
        if (this.currentScanningIndex !== null) {
          // Escaneo simple: asignar al índice específico
          this.newIngreso.items[this.currentScanningIndex].product_id = product.id
          
          // Si tiene datos variables, también llenar los campos correspondientes
          if (barcodeAnalysis?.hasVariableData && barcodeAnalysis.variableData) {
            const vd = barcodeAnalysis.variableData
            
            // Llenar los campos de datos variables en el item actual
            if (vd.lot_number) {
              this.newIngreso.items[this.currentScanningIndex].lot_number = vd.lot_number
            }
            if (vd.serial_number) {
              this.newIngreso.items[this.currentScanningIndex].serial_number = vd.serial_number
            }
            if (vd.expiry_date) {
              this.newIngreso.items[this.currentScanningIndex].expiry_date = vd.expiry_date
            }
          }
          
          // Limpiar currentScanningIndex
          this.currentScanningIndex = null
          
          this.$store.dispatch('showSuccess', `Producto creado y asignado: ${product.name}`)
          
        } else {
          // Escaneo múltiple: agregar a la lista de productos
          if (barcodeAnalysis?.hasVariableData) {
            // Producto con datos variables (GS1)
            this.addToMainProductListWithVariableData(product, barcodeAnalysis)
          } else {
            // Producto simple
            this.addToMainProductList(product)
          }
          
          this.setScanStatus('success', `Producto creado y agregado: ${product.name}`)
          
          // Mantener el foco en el input para continuar escaneando
          this.$nextTick(() => {
            if (this.$refs.multipleScanInput) {
              this.$refs.multipleScanInput.focus()
            }
          })
        }
        
        // Limpiar variables temporales
        this.lastScannedCode = ''
        this.lastExtractedGTIN = ''
        this.lastBarcodeAnalysis = null
        
      } catch (error) {
        console.error('Error agregando producto al ingreso:', error)
        if (this.currentScanningIndex !== null) {
          this.$store.dispatch('showError', 'Error al asignar producto')
        } else {
          this.setScanStatus('error', 'Error al agregar producto al ingreso')
        }
      }
    },

    // Método de prueba específico para parsing de lotes (casos problemáticos)
    testLotParsing() {
      console.log('🧪 === INICIO PRUEBAS LOTES ===')
      
      const lotTestCases = [
        {
          name: 'Lote que funciona correctamente',
          code: '05030267047666172611001012620', // Sin AI 01 inicial para probar solo datos variables
          expected: {
            gtin: '05030267047666',
            expiry_date: '2026-11-00', // Se corregirá día 00 → 01
            lot_number: '1212620' // Completo
          }
        },
        {
          name: 'Lote con 01 interno (problema)',
          code: '050302670476661727010010122154',
          expected: {
            gtin: '05030267047666', 
            expiry_date: '2027-01-00', // Se corregirá día 00 → 01
            lot_number: '1220154' // Debe ser completo, no cortado en "122"
          }
        },
        {
          name: 'Lote con 014 interno (problema)',
          code: '050302670476802806001032312014',
          expected: {
            gtin: '05030267047680',
            expiry_date: '2028-06-00', // Se corregirá día 00 → 01  
            lot_number: '32312014' // Debe ser completo, no cortado en "32312"
          }
        },
        {
          name: 'Código completo con AI 01',
          code: '01050302670476661727010010122154',
          expected: {
            gtin: '05030267047666',
            expiry_date: '2027-01-01', // Corregido
            lot_number: '1220154' // Completo
          }
        }
      ]
      
      lotTestCases.forEach((testCase, index) => {
        console.log(`\n🔍 Prueba Lote ${index + 1}: ${testCase.name}`)
        console.log(`   Código: ${testCase.code}`)
        
        try {
          const result = this.analyzeBarcode(testCase.code)
          console.log('   Resultado:', result)
          
          // Verificar GTIN
          const actualGTIN = result.gtin
          if (actualGTIN === testCase.expected.gtin) {
            console.log(`   ✅ GTIN correcto: ${actualGTIN}`)
          } else {
            console.log(`   ❌ GTIN incorrecto. Esperado: ${testCase.expected.gtin}, Obtenido: ${actualGTIN}`)
          }
          
          // Verificar datos variables si existen
          if (result.variableData) {
            const vd = result.variableData
            
            // Verificar fecha de vencimiento
            if (vd.expiry_date) {
              if (vd.expiry_date === testCase.expected.expiry_date) {
                console.log(`   ✅ Fecha vencimiento correcta: ${vd.expiry_date}`)
              } else {
                console.log(`   ⚠️ Fecha vencimiento. Esperado: ${testCase.expected.expiry_date}, Obtenido: ${vd.expiry_date}`)
              }
            }
            
            // Verificar lote (MUY IMPORTANTE)
            if (vd.lot_number === testCase.expected.lot_number) {
              console.log(`   ✅ LOTE CORRECTO: "${vd.lot_number}"`)
            } else {
              console.log(`   ❌ LOTE INCORRECTO. Esperado: "${testCase.expected.lot_number}", Obtenido: "${vd.lot_number}"`)
              console.log(`   📏 Longitud esperada: ${testCase.expected.lot_number.length}, Obtenida: ${vd.lot_number?.length || 0}`)
            }
          } else {
            console.log('   ❌ No se extrajeron datos variables')
          }
        } catch (error) {
          console.log(`   ❌ Error en prueba: ${error.message}`)
        }
      })
      
      console.log('\n🧪 === FIN PRUEBAS LOTES ===\n')
      console.log('💡 Si algún lote muestra como INCORRECTO, revisar la lógica del parser')
    },

    async processScannedBarcode() {
      const rawBarcode = this.multipleScanInput.trim()
      
      if (!rawBarcode) {
        this.setScanStatus('error', 'Por favor ingrese un código de barras válido')
        return
      }

      this.setScanStatus('loading', 'Analizando código de barras...')

      try {
        console.log('🔍 Scanner input:', rawBarcode)
        
        // Analizar el código de barras para identificar tipo y extraer datos
        const barcodeAnalysis = this.analyzeBarcode(rawBarcode)
        
        console.log('📊 Análisis de código:', barcodeAnalysis)
        console.log('🎯 Datos variables extraídos:', barcodeAnalysis.variableData)
        
        // Buscar producto usando el GTIN extraído (para GS1) o el código completo (para EAN/UPC)
        const searchBarcode = barcodeAnalysis.gtin || rawBarcode
        
        try {
          const product = await this.$store.dispatch('products/findProductByBarcode', searchBarcode)
          
          // Si llegamos aquí, el producto fue encontrado
          this.addScannedProductWithVariableData(product, barcodeAnalysis)
          
          let statusMessage = `${barcodeAnalysis.type}: ${product.name} agregado`
          if (barcodeAnalysis.hasVariableData) {
            const dataInfo = []
            if (barcodeAnalysis.variableData.lot_number) dataInfo.push(`Lote: ${barcodeAnalysis.variableData.lot_number}`)
            if (barcodeAnalysis.variableData.expiry_date) {
              // Determinar qué tipo de fecha se está usando
              if (barcodeAnalysis.variableData.best_before_date && barcodeAnalysis.variableData.expiry_date === barcodeAnalysis.variableData.best_before_date) {
                dataInfo.push(`Cons.Pref: ${barcodeAnalysis.variableData.expiry_date}`)
              } else if (barcodeAnalysis.variableData.packaging_date && barcodeAnalysis.variableData.expiry_date === barcodeAnalysis.variableData.packaging_date) {
                dataInfo.push(`Empaque: ${barcodeAnalysis.variableData.expiry_date}`)
              } else if (barcodeAnalysis.variableData.production_date && barcodeAnalysis.variableData.expiry_date === barcodeAnalysis.variableData.production_date) {
                dataInfo.push(`Prod: ${barcodeAnalysis.variableData.expiry_date}`)
              } else {
                dataInfo.push(`Venc: ${barcodeAnalysis.variableData.expiry_date}`)
              }
            }
            if (barcodeAnalysis.variableData.serial_number) dataInfo.push(`S/N: ${barcodeAnalysis.variableData.serial_number}`)
            if (dataInfo.length > 0) {
              statusMessage += ` (${dataInfo.join(', ')})`
            }
          }
          
          this.setScanStatus('success', statusMessage)
          
          // Limpiar el input para el próximo escaneo
          this.multipleScanInput = ''
          
          // Mantener el foco en el input
          this.$nextTick(() => {
            if (this.$refs.multipleScanInput) {
              this.$refs.multipleScanInput.focus()
            }
          })
          
        } catch (findError) {
          // Verificar si es específicamente un error de producto no encontrado
          if (findError.message === 'Producto no encontrado') {
            console.log('🔍 Producto no encontrado. Analizando tipo de código:', {
              originalCode: rawBarcode,
              searchedBarcode: searchBarcode,
              gtin: barcodeAnalysis.gtin,
              type: barcodeAnalysis.type
            })
            
            // Verificar si es un código GS1 no comercial - mostrar información en lugar del modal
            if (barcodeAnalysis.type === 'GS1' && barcodeAnalysis.variableData && Object.keys(barcodeAnalysis.variableData).length > 0) {
              // Es un código GS1 válido pero no comercial - mostrar información
              this.showCodeInformation(barcodeAnalysis, barcodeAnalysis.variableData)
              
              // Limpiar input y mantener foco para siguiente escaneo
              this.multipleScanInput = ''
              this.$nextTick(() => {
                if (this.$refs.multipleScanInput) {
                  this.$refs.multipleScanInput.focus()
                }
              })
              
            } else if (barcodeAnalysis.type === 'GS1' && barcodeAnalysis.gtin) {
              // Es GS1 pero solo con GTIN - mostrar información básica
              this.showBasicCodeInformation(barcodeAnalysis)
              
              // Limpiar input y mantener foco para siguiente escaneo
              this.multipleScanInput = ''
              this.$nextTick(() => {
                if (this.$refs.multipleScanInput) {
                  this.$refs.multipleScanInput.focus()
                }
              })
              
            } else {
              // Código no GS1 o sin datos válidos - mostrar modal de creación
              // Guardar información del código para el modal
              this.lastScannedCode = rawBarcode
              this.lastExtractedGTIN = searchBarcode
              this.lastBarcodeAnalysis = barcodeAnalysis
              
              // Limpiar input
              this.multipleScanInput = ''
              
              // Mostrar modal de confirmación
              this.showCreateProductConfirm = true
            }
          } else {
            // Error real de búsqueda (conexión, etc.)
            throw findError
          }
        }
        
      } catch (error) {
        console.error('Error procesando código de barras:', error)
        this.setScanStatus('error', 'Error al procesar el código de barras')
      }
    },

    addScannedProduct(product, barcode) {
      // Mantener método original para compatibilidad con escaneo simple
      const barcodeAnalysis = { type: 'Simple', gtin: barcode, original: barcode, variableData: null }
      this.addScannedProductWithVariableData(product, barcodeAnalysis)
    },

    addScannedProductWithVariableData(product, barcodeAnalysis) {
      // Preparar datos para comparación
      const variableData = barcodeAnalysis.variableData || {}
      
      // Para fechas: usar solo lo que viene en el código GS1, NO asignar fecha automática
      // Para códigos EAN-13 sin datos variables, estos campos quedarán vacíos
      const expiryDate = variableData.expiry_date || variableData.production_date || ''
      const lotNumber = variableData.lot_number || ''
      const serialNumber = variableData.serial_number || ''
      
      // Verificar si el producto ya existe con EXACTAMENTE los mismos datos
      const existingProductIndex = this.scannedProducts.findIndex(p => {
        const pVariableData = p.variableData || {}
        const pExpiryDate = pVariableData.expiry_date || pVariableData.production_date || ''
        const pLotNumber = pVariableData.lot_number || ''
        const pSerialNumber = pVariableData.serial_number || ''
        
        const matches = (
          p.id === product.id &&
          pLotNumber === lotNumber &&
          pExpiryDate === expiryDate &&
          pSerialNumber === serialNumber
        )
        
        // No consolidar si tiene serial number y ya hay cantidad > 0
        if (matches && serialNumber && p.quantity >= 1) {
          return false
        }
        
        return matches
      })
      
      if (existingProductIndex !== -1) {
        // Si existe con los mismos datos, incrementar la cantidad por cantidad_x_caja
        const incrementAmount = product.cantidad_x_caja || 1
        
        if (window.debugGS1Consolidation) {
          console.log('📋 Incrementando cantidad en scannedProducts:', {
            product: product.name,
            current: this.scannedProducts[existingProductIndex].quantity,
            increment: incrementAmount,
            new: this.scannedProducts[existingProductIndex].quantity + incrementAmount
          })
        }
        this.scannedProducts[existingProductIndex].quantity += incrementAmount
        this.lastScannedProduct = { ...this.scannedProducts[existingProductIndex], wasIncremented: true, barcodeAnalysis }
      } else {
        // Si no existe, agregarlo como nuevo
        // Usar cantidad_x_caja si está disponible, sino default a 1
        const initialQuantity = product.cantidad_x_caja || 1
        
        console.log('📊 Campo cantidad_x_caja:', product.cantidad_x_caja)
        console.log('✅ Nuevo producto agregado con cantidad inicial de', initialQuantity, `(cantidad_x_caja: ${product.cantidad_x_caja})`)
        
        const newProduct = {
          id: product.id,
          name: product.name,
          sku: product.sku,
          barcode: barcodeAnalysis.gtin || barcodeAnalysis.original,
          barcode_original: barcodeAnalysis.original,
          barcode_type: barcodeAnalysis.type,
          quantity: initialQuantity,
          variableData: barcodeAnalysis.variableData
        }
        if (window.debugGS1Consolidation) {
          console.log('📋 Agregando nuevo producto a scannedProducts:', {
            product: product.name,
            lot: lotNumber,
            expiry: expiryDate,
            serial: serialNumber
          })
        }
        this.scannedProducts.push(newProduct)
        this.lastScannedProduct = { ...newProduct, wasIncremented: false, barcodeAnalysis }
      }

      // Agregar automáticamente a la lista principal del ingreso con datos variables
      this.addToMainProductListWithVariableData(product, barcodeAnalysis)
      
      // Emitir sonido de éxito (si está disponible)
      this.playSuccessSound()
    },

    addToMainProductList(product) {
      // Mantener método original para compatibilidad
      const barcodeAnalysis = { variableData: null }
      this.addToMainProductListWithVariableData(product, barcodeAnalysis)
    },

    addToMainProductListWithVariableData(product, barcodeAnalysis) {
      // NUEVA LÓGICA DE CONSOLIDACIÓN INTELIGENTE:
      // - Para códigos idénticos (mismo producto, lote, fecha, serial) → incrementar cantidad
      // - Para códigos diferentes (distinto lote o fecha) → crear nuevo item  
      // - Manejo especial para seriales: no consolidar si ya existe (cada serial es único)
      
      const variableData = barcodeAnalysis.variableData || {}
      
      // Para fechas: usar solo lo que viene en el código GS1, NO asignar fecha automática
      // Para códigos EAN-13 sin datos variables, estos campos quedarán vacíos
      let expiryDate = variableData.expiry_date || variableData.production_date || ''
      const lotNumber = variableData.lot_number || ''
      const serialNumber = variableData.serial_number || ''
      
      // Debug detallado solo si está habilitado
      if (window.debugGS1Consolidation) {
        console.log('📦 Buscando producto existente:', {
          product_id: product.id,
          product_name: product.name,
          lot_number: lotNumber,
          expiry_date: expiryDate,
          serial_number: serialNumber,
          current_items: this.newIngreso.items.length
        })
      }
      
      // Buscar item existente que coincida EXACTAMENTE en todos los campos relevantes
      const existingItemIndex = this.newIngreso.items.findIndex(item => {
        // Normalizar fecha de vencimiento para comparación consistente (ambos deben ser '' si están vacíos)
        const itemExpiryDate = item.expiry_date || ''
        
        const itemMatches = (
          item.product_id === product.id &&
          (item.lot_number || '') === lotNumber &&
          itemExpiryDate === expiryDate &&
          (item.serial_number || '') === serialNumber
        )
        
        // Para productos con serial number, no consolidar si cantidad > 1
        // (cada serial debe ser único)
        if (itemMatches && serialNumber && item.quantity >= 1) {
          return false // No consolidar items con serial number
        }
        
        return itemMatches
      })
      
      if (existingItemIndex !== -1) {
        // Item existente encontrado - incrementar cantidad por cantidad_x_caja
        const incrementAmount = product.cantidad_x_caja || 1
        
        if (window.debugGS1Consolidation) {
          console.log('✅ Producto existente encontrado, incrementando cantidad:', {
            index: existingItemIndex,
            product_name: product.name,
            current_quantity: this.newIngreso.items[existingItemIndex].quantity,
            increment: incrementAmount,
            new_quantity: this.newIngreso.items[existingItemIndex].quantity + incrementAmount,
            lot_number: lotNumber,
            expiry_date: expiryDate
          })
        }
        
        this.newIngreso.items[existingItemIndex].quantity += incrementAmount
        return
      }
      
      // No se encontró item existente - crear nuevo
      if (window.debugGS1Consolidation) {
        console.log('📦 Creando nuevo item con datos:', {
          product: product.name,
          lot_number: lotNumber,
          expiry_date: expiryDate,
          serial_number: serialNumber,
          production_date: variableData.production_date,
          items_before: this.newIngreso.items.length
        })
      }
      
      // Buscar si hay una fila vacía disponible (product_id === null)
      const emptyItemIndex = this.newIngreso.items.findIndex(item => item.product_id === null)
      
      const hasVariableData = barcodeAnalysis.variableData && Object.keys(barcodeAnalysis.variableData).length > 0
      
      // Obtener la cantidad del producto desde scannedProducts (que ya tiene cantidad_x_caja aplicada)
      const scannedProduct = this.scannedProducts.find(p => {
        const pVariableData = p.variableData || {}
        const pExpiryDate = pVariableData.expiry_date || pVariableData.production_date || ''
        const pLotNumber = pVariableData.lot_number || ''
        const pSerialNumber = pVariableData.serial_number || ''
        
        return (
          p.id === product.id &&
          pLotNumber === lotNumber &&
          pExpiryDate === expiryDate &&
          pSerialNumber === serialNumber
        )
      })
      
      const itemQuantity = scannedProduct ? scannedProduct.quantity : (product.cantidad_x_caja || 1)
      
      console.log('📋 Usando fila vacía existente en posición:', emptyItemIndex)
      console.log('📦 Cantidad del item a agregar:', itemQuantity)
      
      const newItem = {
        product_id: product.id,
        quantity: itemQuantity,
        lot_number: lotNumber,
        expiry_date: expiryDate,
        serial_number: serialNumber,
        productSearch: '',
        // Campos de dispositivos médicos
        forma_adquisicion: '',
        fecha_recepcion_conforme: '',
        numero_registro_sanitario: '',
        numero_inventario_activo_fijo: '',
        // Metadatos del código de barras (para referencia)
        barcode_type: barcodeAnalysis.type,
        barcode_original: barcodeAnalysis.original,
        auto_filled: hasVariableData, // Indicar si los datos fueron llenados automáticamente
        production_date: variableData.production_date || '' // Mantener fecha de producción si existe
      }
      
      if (emptyItemIndex !== -1) {
        // Si hay una fila vacía, usarla en lugar de crear una nueva
        console.log('📋 Usando fila vacía existente en posición:', emptyItemIndex)
        this.newIngreso.items[emptyItemIndex] = newItem
      } else {
        // Si no hay filas vacías, agregar una nueva
        console.log('📋 Agregando nueva fila')
        this.newIngreso.items.push(newItem)
      }
    },

    undoLastScan() {
      if (!this.lastScannedProduct) {
        this.setScanStatus('error', 'No hay escaneo que deshacer')
        return
      }

      const productToUndo = this.lastScannedProduct
      
      // Encontrar el producto en la lista escaneada
      const scannedIndex = this.scannedProducts.findIndex(p => p.id === productToUndo.id)
      
      if (scannedIndex !== -1) {
        if (productToUndo.wasIncremented && this.scannedProducts[scannedIndex].quantity > 1) {
          // Si fue incrementado, reducir la cantidad
          this.scannedProducts[scannedIndex].quantity -= 1
        } else {
          // Si era nuevo, eliminarlo completamente
          this.scannedProducts.splice(scannedIndex, 1)
        }
      }

      // También deshacer en la lista principal
      const mainIndex = this.newIngreso.items.findIndex(item => item.product_id === productToUndo.id)
      
      if (mainIndex !== -1) {
        if (this.newIngreso.items[mainIndex].quantity > 1) {
          this.newIngreso.items[mainIndex].quantity -= 1
        } else {
          // Solo eliminar si no es el único item y si quantity es 1
          if (this.newIngreso.items.length > 1) {
            this.newIngreso.items.splice(mainIndex, 1)
          } else {
            // Si es el único item, resetear a valores por defecto
            this.newIngreso.items[mainIndex] = {
              product_id: null,
              quantity: 1,
              lot_number: '',
              expiry_date: '',
              serial_number: '',
              productSearch: '',
              // Campos de dispositivos médicos
              forma_adquisicion: '',
              fecha_recepcion_conforme: '',
              numero_registro_sanitario: '',
              numero_inventario_activo_fijo: ''
            }
          }
        }
      }

      this.setScanStatus('warning', `Deshecho: ${productToUndo.name}`)
      this.lastScannedProduct = null
    },

    clearScannedProducts() {
      const confirmClear = confirm('¿Está seguro que desea limpiar todos los productos escaneados?')
      
      if (confirmClear) {
        // Limpiar productos escaneados
        this.scannedProducts = []
        this.lastScannedProduct = null
        
        // Resetear la lista principal a un item vacío
        this.newIngreso.items = [
          { 
            product_id: null, 
            quantity: 1, 
            lot_number: '', 
            expiry_date: '', 
            serial_number: '', 
            productSearch: '',
            // Campos de dispositivos médicos
            forma_adquisicion: '',
            fecha_recepcion_conforme: '',
            numero_registro_sanitario: '',
            numero_inventario_activo_fijo: ''
          }
        ]
        
        this.setScanStatus('info', 'Todos los productos han sido eliminados')
        
        // Volver a enfocar el input si está en modo escaneo múltiple
        if (this.isMultipleScanMode) {
          this.$nextTick(() => {
            if (this.$refs.multipleScanInput) {
              this.$refs.multipleScanInput.focus()
            }
          })
        }
      }
    },

    setScanStatus(type, message) {
      this.scanStatus = { type, message }
      
      // Auto-limpiar mensajes después de unos segundos
      if (type !== 'loading') {
        setTimeout(() => {
          this.scanStatus = { type: '', message: '' }
        }, 3000)
      }
    },

    playSuccessSound() {
      // Intentar reproducir un sonido de éxito
      try {
        const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhCKDW8tl+KwUu')
        audio.volume = 0.3
        audio.play().catch(() => {
          // Silenciar errores de audio si no se puede reproducir
        })
      } catch (error) {
        // Silenciar errores de audio
      }
    },

    // ========== MÉTODOS DE IMPRESIÓN ==========

    // Obtener código de barras de un producto por ID
    getProductBarcode(productId) {
      if (!productId || !this.products) return null
      const product = this.products.find(p => p.id === productId)
      return product?.barcode || null
    },

    // Verificar si un código de barras es EAN-13
    isEAN13(barcode) {
      if (!barcode) return false
      // EAN-13 debe tener exactamente 13 dígitos y empezar por 789 (nuestro patrón)
      return /^789\d{10}$/.test(barcode.toString())
    },

    // Verificar si un producto es un Dispositivo Médico Priorizado
    isProductDMPriorizado(productId) {
      if (!productId || !this.products || !Array.isArray(this.products)) return false
      const product = this.products.find(p => p && p.id === productId)
      
      // Log específico para DM06 para debug
      if (product && product.sku === 'DM06') {
        console.log('🩺 DM06 detectado:', { es_dm_priorizado: product.es_dm_priorizado, tipo_dm: product.tipo_dm })
      }
      
      return product?.es_dm_priorizado || false
    },

    // Obtener información completa del producto
    getProductInfo(productId) {
      if (!productId || !this.products || !Array.isArray(this.products)) return null
      return this.products.find(p => p && p.id === productId) || null
    },

    // Verificar si el producto requiere registro sanitario
    getProductRequiereRegistroSanitario(productId) {
      const product = this.getProductInfo(productId)
      return product?.requiere_registro_sanitario === true
    },

    // Mostrar modal de impresión para un producto
    showPrintProductDialog(productId, quantity = 1) {
      if (!productId || !this.products) return
      
      const product = this.products.find(p => p.id === productId)
      if (!product || !product.barcode) {
        this.$store.dispatch('showError', 'Producto no tiene código de barras válido')
        return
      }

      if (!this.isEAN13(product.barcode)) {
        this.$store.dispatch('showError', 'Solo se pueden imprimir códigos EAN-13 generados automáticamente')
        return
      }

      // Agregar la cantidad al producto para impresión
      this.selectedProductForPrint = { ...product, quantity: quantity }
      this.showPrintDialog = true
      
      // Generar vista previa después de que el modal se abra
      this.$nextTick(() => {
        this.generatePrintPreview()
      })
    },

    // Generar vista previa de códigos de barras
    generatePrintPreview() {
      if (!this.selectedProductForPrint?.barcode) return
      
      const barcode = this.selectedProductForPrint.barcode
      const copies = this.selectedProductForPrint.quantity || 1
      
      // Usar setTimeout para asegurar que los canvas estén en el DOM
      setTimeout(() => {
        for (let i = 1; i <= copies; i++) {
          const canvasArray = this.$refs[`previewCanvas${i}`]
          const canvas = Array.isArray(canvasArray) ? canvasArray[0] : canvasArray
          
          if (canvas && JsBarcode) {
            try {
              // Configuración responsiva según el tamaño de etiqueta
              const barcodeConfig = {
                format: "EAN13",
                displayValue: true,
                background: "#ffffff",
                lineColor: "#000000"
              }
              
              // Ajustar según el tamaño de etiqueta
              switch (this.printSettings.labelSize) {
                case 'small':
                  barcodeConfig.width = 1
                  barcodeConfig.height = 30
                  barcodeConfig.fontSize = 8
                  barcodeConfig.margin = 2
                  break
                case 'medium':
                  barcodeConfig.width = 1.2
                  barcodeConfig.height = 40
                  barcodeConfig.fontSize = 10
                  barcodeConfig.margin = 3
                  break
                case 'large':
                  barcodeConfig.width = 1.5
                  barcodeConfig.height = 50
                  barcodeConfig.fontSize = 12
                  barcodeConfig.margin = 4
                  break
                default:
                  barcodeConfig.width = 1.2
                  barcodeConfig.height = 40
                  barcodeConfig.fontSize = 10
                  barcodeConfig.margin = 3
              }
              
              JsBarcode(canvas, barcode, barcodeConfig)
            } catch (error) {
              console.error('Error generando preview de código de barras:', error)
            }
          }
        }
      }, 100)
    },

    // Generar imagen de código de barras como base64
    async generateBarcodeImage(barcodeText, labelSize = 'medium') {
      return new Promise((resolve) => {
        const canvas = document.createElement('canvas')
        try {
          if (JsBarcode) {
            // Configuración responsiva según el tamaño de etiqueta
            const barcodeConfig = {
              format: "EAN13",
              displayValue: true,
              background: "#ffffff",
              lineColor: "#000000"
            }
            
            // Ajustar según el tamaño de etiqueta
            switch (labelSize) {
              case 'small':
                barcodeConfig.width = 1
                barcodeConfig.height = 30
                barcodeConfig.fontSize = 8
                barcodeConfig.margin = 2
                break
              case 'medium':
                barcodeConfig.width = 1.2
                barcodeConfig.height = 40
                barcodeConfig.fontSize = 10
                barcodeConfig.margin = 3
                break
              case 'large':
                barcodeConfig.width = 1.5
                barcodeConfig.height = 50
                barcodeConfig.fontSize = 12
                barcodeConfig.margin = 4
                break
              default:
                barcodeConfig.width = 1.2
                barcodeConfig.height = 40
                barcodeConfig.fontSize = 10
                barcodeConfig.margin = 3
            }
            
            JsBarcode(canvas, barcodeText, barcodeConfig)
            
            setTimeout(() => {
              const dataURL = canvas.toDataURL('image/png')
              resolve(dataURL)
            }, 100)
          } else {
            resolve('')
          }
        } catch (error) {
          console.error('Error generating barcode image:', error)
          resolve('')
        }
      })
    },

    // Imprimir códigos de barras del producto
    async printProductBarcode() {
      if (!this.selectedProductForPrint || !this.selectedProductForPrint.barcode) return
      
      try {
        this.printing = true
        
              const product = this.selectedProductForPrint
      const copies = product.quantity || 1
        
        // Crear lista de "productos" para usar las funciones existentes de impresión
        const productsToPrint = []
        for (let i = 0; i < copies; i++) {
          productsToPrint.push({
            id: product.id,
            name: product.name,
            sku: product.sku,
            barcode: product.barcode
          })
        }
        
        // Generar imágenes de códigos de barras
        const productsWithBarcodes = await Promise.all(
          productsToPrint.map(async (prod) => {
            const barcodeImage = await this.generateBarcodeImage(prod.barcode, this.printSettings.labelSize)
            
            return {
              ...prod,
              barcodeImage: barcodeImage
            }
          })
        )
        
        let printContent = ''
        
        if (this.printSettings.printerType === 'standard') {
          printContent = this.generateStandardPrintContent(productsWithBarcodes)
        } else {
          printContent = this.generateLabelPrintContent(productsWithBarcodes)
        }
        
        // Abrir ventana de impresión
        const printWindow = window.open('', '_blank')
        printWindow.document.write(printContent)
        printWindow.document.close()
        printWindow.focus()
        
        setTimeout(() => {
          printWindow.print()
          printWindow.close()
        }, 500)
        
        this.showPrintDialog = false
        this.$store.dispatch('showSuccess', `${copies} etiqueta(s) enviada(s) a impresión`)
        
      } catch (error) {
        console.error('Error imprimiendo códigos de barras:', error)
        this.$store.dispatch('showError', 'Error al imprimir códigos de barras')
      } finally {
        this.printing = false
      }
    },

    // Generar contenido HTML para impresión estándar
    generateStandardPrintContent(productsList) {
      let content = `
        <html>
          <head>
            <title>Códigos de Barras - ${this.selectedProductForPrint.name}</title>
            <style>
              @page {
                margin: 1cm;
                size: A4 portrait;
              }
              
              body { 
                font-family: Arial, sans-serif; 
                margin: 0; 
                padding: 0; 
                background: white;
                max-width: 19cm; /* A4 - márgenes */
              }
              
              .print-grid { 
                display: grid; 
                grid-template-columns: repeat(${this.printSettings.labelsPerRow}, minmax(0, 1fr)); 
                gap: 8px; 
                width: 100%;
                max-width: 100%;
                justify-content: center;
                padding: 10px;
              }
              
              .barcode-label { 
                border: 1px solid #ddd; 
                padding: 8px; 
                text-align: center; 
                page-break-inside: avoid;
                box-sizing: border-box;
                overflow: hidden;
              }
              
              /* Tamaños responsivos que se adaptan al grid */
              .barcode-label.small { 
                min-width: 1.5cm; 
                max-width: 3cm;
                height: 1.2cm; 
                font-size: 7px; 
              }
              .barcode-label.medium { 
                min-width: 3cm; 
                max-width: 5cm;
                height: 2.5cm; 
                font-size: 9px; 
              }
              .barcode-label.large { 
                min-width: 4cm; 
                max-width: 7cm;
                height: 3.5cm; 
                font-size: 11px; 
              }
              

              
              .barcode-image { 
                max-width: 95%; 
                height: auto; 
                margin: 2px 0;
                display: block;
                margin-left: auto;
                margin-right: auto;
              }
              
              .barcode-text { 
                font-family: monospace; 
                font-size: 0.8em; 
                color: #333; 
                margin-top: 2px;
                word-break: break-all;
              }
              
              /* Estilos específicos para impresión */
              @media print {
                body { 
                  margin: 0 !important;
                  padding: 0 !important;
                }
                .print-grid {
                  padding: 5px !important;
                }
                .barcode-label {
                  break-inside: avoid;
                  border: 1px solid #000 !important;
                }
              }
            </style>
          </head>
          <body>
            <div class="print-grid">
      `
      
      productsList.forEach((product) => {
        content += `
          <div class="barcode-label ${this.printSettings.labelSize}">
            ${product.barcodeImage ? `<img src="${product.barcodeImage}" class="barcode-image" alt="${product.barcode}">` : ''}
            <div class="barcode-text">${product.barcode}</div>
          </div>
        `
      })
      
      content += `
            </div>
          </body>
        </html>
      `
      
      return content
    },

    // Generar contenido HTML para impresión de etiquetas
    generateLabelPrintContent(productsList) {
      let content = `
        <html>
          <head>
            <title>Etiquetas - ${this.selectedProductForPrint.name}</title>
            <style>
              body { font-family: Arial, sans-serif; margin: 0; padding: 0; }
              .label { 
                width: ${this.printSettings.labelFormat === '2x1' ? '2in' : 
                         this.printSettings.labelFormat === '4x2' ? '4in' : '4in'}; 
                height: ${this.printSettings.labelFormat === '2x1' ? '1in' : 
                          this.printSettings.labelFormat === '4x2' ? '2in' : '6in'}; 
                border: 1px solid #000; 
                margin: 5px; 
                padding: 10px; 
                text-align: center;
                display: inline-block;
                page-break-inside: avoid;
              }

              .barcode-image { max-width: 90%; height: auto; margin: 5px 0; }
              .barcode-text { font-family: monospace; font-size: 10px; color: #666; }
            </style>
          </head>
          <body>
      `
      
      productsList.forEach((product) => {
        content += `
          <div class="label">
            ${product.barcodeImage ? `<img src="${product.barcodeImage}" class="barcode-image" alt="${product.barcode}">` : ''}
            <div class="barcode-text">${product.barcode}</div>
          </div>
        `
      })
      
      content += `
          </body>
        </html>
      `
      
      return content
    },

    // ========== FUNCIONES INFORMATIVAS PARA CÓDIGOS NO COMERCIALES ==========
    
    showCodeInformation(analysis, productData) {
      const codeType = this.identifyCodeType(analysis, productData)
      const details = this.formatCodeDetails(productData)
      
      const message = `📊 Código ${codeType} detectado:\n${details}\n\n⚡ Listo para siguiente escaneo`
      
      // Mostrar mensaje informativo usando Vuex
      this.$store.dispatch('showInfo', message)
      
      // También usar setScanStatus para consistencia
      this.setScanStatus('info', `Código ${codeType} parseado correctamente`)
      
      console.log(`📊 [INFO] Código no comercial parseado:`, {
        type: codeType,
        gtin: analysis.gtin,
        data: productData
      })
    },
    
    showBasicCodeInformation(analysis) {
      const codeType = this.identifyCodeTypeByGTIN(analysis.gtin)
      const message = `📦 Código ${codeType} detectado:\n🏷️ GTIN: ${analysis.gtin}\n\n⚡ Listo para siguiente escaneo`
      
      // Mostrar mensaje informativo usando Vuex
      this.$store.dispatch('showInfo', message)
      
      // También usar setScanStatus para consistencia
      this.setScanStatus('info', `Código ${codeType} parseado correctamente`)
      
      console.log(`📦 [INFO] Código GS1 básico:`, {
        type: codeType,
        gtin: analysis.gtin
      })
    },
    
    identifyCodeType(analysis, productData) {
      // Identificar tipo de código basado en los AIs presentes
      if (productData.sscc) return 'CONTENEDOR (SSCC)'
      if (productData.gsin) return 'ENVÍO (GSIN)'  
      if (productData.gdti) return 'DOCUMENTO (GDTI)'
      if (productData.gln) return 'UBICACIÓN (GLN)'
      if (productData.gross_weight_kg || productData.net_weight_kg) return 'PRODUCTO CON PESO'
      if (productData.custom_field_90 || productData.custom_field_91) return 'CÓDIGO PERSONALIZADO'
      if (analysis.gtin) return 'PRODUCTO GS1'
      
      return 'GS1'
    },
    
    identifyCodeTypeByGTIN(gtin) {
      if (!gtin) return 'GS1'
      
      // Análisis básico del GTIN para identificar tipo
      if (gtin.startsWith('00')) return 'CONTENEDOR/PALLET'
      if (gtin.startsWith('02')) return 'PRODUCTO VARIABLE'
      if (gtin.startsWith('1') || gtin.startsWith('2')) return 'PRODUCTO COMERCIAL'
      
      return 'PRODUCTO GS1'
    },
    
    formatCodeDetails(productData) {
      const details = []
      
      // Información principal
      if (productData.sscc) details.push(`📦 SSCC: ${productData.sscc}`)
      if (productData.gsin) details.push(`🚛 GSIN: ${productData.gsin}`)
      if (productData.gdti) details.push(`📄 GDTI: ${productData.gdti}`)
      if (productData.gln) details.push(`🏢 GLN: ${productData.gln}`)
      
      // Información de producto
      if (productData.lot_number) details.push(`🏷️ Lote: ${productData.lot_number}`)
      if (productData.serial_number) details.push(`🔢 Serie: ${productData.serial_number}`)
      if (productData.expiry_date) details.push(`📅 Vence: ${this.formatDate(productData.expiry_date)}`)
      if (productData.production_date) details.push(`🏭 Prod.: ${this.formatDate(productData.production_date)}`)
      
      // Pesos
      if (productData.gross_weight_kg) details.push(`⚖️ Peso bruto: ${productData.gross_weight_kg}kg`)
      if (productData.net_weight_kg) details.push(`⚖️ Peso neto: ${productData.net_weight_kg}kg`)
      if (productData.gross_weight_lb) details.push(`⚖️ Peso bruto: ${productData.gross_weight_lb}lb`)
      if (productData.net_weight_lb) details.push(`⚖️ Peso neto: ${productData.net_weight_lb}lb`)
      
      // Campos personalizados
      const customFields = ['custom_field_90', 'custom_field_91', 'custom_field_92', 
                           'custom_field_93', 'custom_field_94', 'custom_field_95',
                           'custom_field_96', 'custom_field_97', 'custom_field_98', 'custom_field_99']
      
      customFields.forEach((field, index) => {
        if (productData[field]) {
          details.push(`🔧 Campo ${90 + index}: ${productData[field]}`)
        }
      })
      
      return details.length > 0 ? details.join('\n') : '📋 Información GS1 detectada'
    }
  },
  watch: {
    // Regenerar vista previa cuando cambian los ajustes de impresión
    'printSettings.printerType'() {
      if (this.showPrintDialog) {
        this.$nextTick(() => this.generatePrintPreview())
      }
    },
    'printSettings.labelSize'() {
      if (this.showPrintDialog) {
        this.$nextTick(() => this.generatePrintPreview())
      }
    },
    'selectedProductForPrint.quantity'() {
      if (this.showPrintDialog) {
        this.$nextTick(() => this.generatePrintPreview())
      }
    }
  },
  mounted() {
    this.fetchIngresos()
    this.fetchProducts()
    this.fetchLocations()
    this.fetchZones()
    this.fetchWarehouses()
    
          // Ejecutar pruebas GS1 en desarrollo (solo si está habilitado por consola)
      // Para habilitar: ejecutar window.enableGS1Tests = true en la consola
      // Para pruebas de lotes: window.enableLotTests = true
      if (process.env.NODE_ENV === 'development' && window.enableGS1Tests) {
        setTimeout(() => {
          this.testGS1Parsing()
        }, 1000)
      }
      if (process.env.NODE_ENV === 'development' && window.enableLotTests) {
        setTimeout(() => {
          this.testLotParsing()
        }, 1500)
      }
  },
  beforeUnmount() {
    this.stopScanner()
  }
}
</script>

<style scoped>
.ingresos-container {
  padding: 20px;
}

/* Estilos para la tabla de ingresos */
.ingresos-table {
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  background: white !important;
}

.ingresos-table .v-data-table__thead {
  background-color: #f8f9fa !important;
}

.ingresos-table .v-data-table__thead .v-data-table__th {
  background-color: #f8f9fa !important;
  font-weight: 600 !important;
  color: #495057 !important;
  border-bottom: 2px solid #dee2e6 !important;
  padding: 12px 16px !important;
  font-size: 0.875rem !important;
}

.ingresos-table .v-data-table__tbody .v-data-table__td {
  padding: 12px 16px !important;
  border-bottom: 1px solid #dee2e6 !important;
}

.ingresos-table .v-data-table__tbody tr:hover {
  background-color: #f8f9fa !important;
}

.detail-group {
  margin-bottom: 1rem;
}

.detail-group label {
  font-weight: 500;
  color: rgba(0, 0, 0, 0.6);
  margin-bottom: 0.25rem;
  display: block;
}

.detail-group p {
  margin: 0;
}

.v-data-table {
  background: transparent !important;
}

.v-card {
  border-radius: 8px;
}

.v-card-title {
  font-size: 1.25rem;
  font-weight: 500;
}

.v-card-text {
  padding-top: 20px;
}

.v-divider {
  margin: 16px 0;
}

.text-h6 {
  font-size: 1.25rem;
  font-weight: 500;
  margin-bottom: 16px;
}

.v-btn {
  text-transform: none;
}

.v-alert {
  margin-top: 16px;
}

.v-chip {
  font-weight: 500;
}

.v-table {
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 4px;
}

.v-table th {
  font-weight: 500;
  color: rgba(0, 0, 0, 0.87);
  background-color: #f5f5f5;
}

.v-table td {
  color: rgba(0, 0, 0, 0.87);
}

.v-table tr:hover {
  background-color: #f5f5f5;
}

.barcode-scanner {
  width: 100%;
  max-width: 400px;
  height: 300px;
  object-fit: cover;
  border-radius: 8px;
}

/* Estilos para el modal de configuración de almacenamiento */
.switch-container {
  padding: 16px;
  background-color: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e9ecef;
}

.switch-container .v-switch {
  margin-bottom: 0;
}

.switch-container .v-switch .v-selection-control__wrapper {
  height: auto;
}

/* Mejorar la apariencia del switch */
.v-switch .v-selection-control__input {
  border-radius: 12px;
}

.v-switch .v-selection-control__input:before {
  border-radius: 12px;
}

/* Asegurar que el switch se vea habilitado cuando está activo */
.v-switch.v-input--is-dirty .v-selection-control__input {
  color: rgb(var(--v-theme-primary)) !important;
}

.v-switch.v-input--is-dirty .v-selection-control__input:before {
  background-color: rgb(var(--v-theme-primary)) !important;
  opacity: 0.2;
}

.v-switch.v-input--is-dirty .v-selection-control__thumb {
  background-color: rgb(var(--v-theme-primary)) !important;
  color: white !important;
}

/* Estilos para las alertas del modal */
.v-alert ul {
  margin-left: 16px;
  margin-bottom: 0;
}

.v-alert li {
  margin-bottom: 4px;
}

/* Estilos para impresión de códigos de barras */
.print-preview-container {
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 16px;
  background: #f9f9f9;
  min-height: 120px;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: center;
}

.print-preview-container.standard {
  max-height: 300px;
  overflow-y: auto;
}

.preview-label {
  border: 1px solid #ccc;
  border-radius: 4px;
  padding: 8px;
  text-align: center;
  background: white;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
}

.preview-label.small { 
  width: 80px; 
  height: 40px; 
  font-size: 8px; 
}

.preview-label.medium { 
  width: 120px; 
  height: 60px; 
  font-size: 10px; 
}

.preview-label.large { 
  width: 160px; 
  height: 80px; 
  font-size: 12px; 
}



.preview-barcode-canvas {
  max-width: 100%;
  height: auto;
}

.barcode-text {
  font-family: 'Courier New', monospace;
  font-size: 0.75em;
  color: #666;
  margin-top: 2px;
}

/* Responsive Design */
@media (max-width: 768px) {
  .ingresos-container {
    padding: 10px;
  }
  
  .ingresos-title {
    font-size: 1.1rem !important;
    margin-bottom: 8px;
  }
  
  /* Filtros responsivos */
  .ingresos-filters {
    margin-bottom: 16px !important;
  }
  
  .ingresos-filters .v-col {
    padding: 4px 8px !important;
  }
  
  .v-text-field, .v-select {
    margin-bottom: 8px;
  }
  
  .v-text-field .v-field__input,
  .v-select .v-field__input {
    font-size: 14px !important;
    padding: 8px 12px !important;
  }
  
  /* Cards para resumen */
  .v-card-title {
    font-size: 1rem !important;
    padding: 12px 16px 8px !important;
  }
  
  .v-card-text {
    padding: 8px 16px 16px !important;
  }
  
  /* Optimización de espacios */
  .v-row {
    margin: 0 -8px !important;
  }
  
  .v-col {
    padding: 0 8px !important;
  }
  
  /* Modal responsivo */
  .v-dialog .v-card {
    margin: 16px !important;
    width: calc(100% - 32px) !important;
    max-width: none !important;
  }
  
  .v-dialog .v-card-title {
    font-size: 1.1rem !important;
    padding: 16px !important;
  }
  
  .v-dialog .v-card-text {
    padding: 0 16px 16px !important;
  }
}

@media (max-width: 480px) {
  .ingresos-container {
    padding: 8px;
  }
  
  .ingresos-title {
    font-size: 1rem !important;
  }
  
  /* Botones más pequeños en móvil */
  .v-btn {
    min-width: 40px !important;
    height: 36px !important;
  }
  
  /* Chips más pequeños */
  .v-chip {
    font-size: 0.75rem !important;
  }
}
</style>
